import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Home, ChevronRight, ChevronDown, Shield, QrCode, Trophy,
  Globe, Star, Gift, Bath, Link2, Bell, Map, MessageCircle, Users, BarChart3, Sparkles,
  BookOpen, Compass, Wifi, ShoppingBag, Zap, CheckCircle2,
  ArrowRight, Phone, Heart, User, MapPin, Calendar,
  Search, Flame, Newspaper, Plane, Car, ShieldCheck, Eye,
  Volume2, VolumeX, BadgePercent, Menu, X, Store, Clock, Languages, Mic,
  FileText, Send, Video, CreditCard, Download, Wallet
} from "lucide-react";
import { useAuth, API } from "../App";
import LanguageSelector from "../components/LanguageSelector";
import HomeHero from "../components/HomeHero";
import ComuneAutocomplete from "../components/ComuneAutocomplete";
import SitePopup from "../components/SitePopup";
import { useLanguage } from "../contexts/LanguageContext";
import { siteFeatures, guestAppFeatures, faqs, getT } from "../data/landingContent";
import axios from "axios";

const ACCENT = "#d04260";
const ACCENT_DARK = "#b91c47";
const AMBER = "#F59E0B";

// Mini Italian flag (green/white/red) for the "Made in Italy" trust stat.
const ItalyFlag = () => (
  <span className="inline-flex h-3 w-[18px] md:h-5 md:w-7 rounded-[3px] overflow-hidden ring-1 ring-black/10" aria-label="Italia">
    <span className="flex-1" style={{ background: "#008C45" }} />
    <span className="flex-1 bg-white" />
    <span className="flex-1" style={{ background: "#CD212A" }} />
  </span>
);

// Small 3-step podium glyph (uses currentColor) for the "Classifica Host" CTA.
const MiniPodium = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <rect x="1" y="12" width="6" height="9" rx="1.2" />
    <rect x="9" y="6" width="6" height="15" rx="1.2" />
    <rect x="17" y="14" width="6" height="7" rx="1.2" />
  </svg>
);

// Podio animato — le barre "respirano" in loop per attirare l'occhio sul tasto classifica.
const AnimatedPodium = ({ className = "w-5 h-5" }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
    animate={{ rotate: [0, -5, 5, 0] }}
    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
  >
    <motion.rect x="1" width="6" rx="1.2" animate={{ y: [13, 12, 13], height: [8, 9, 8] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.25 }} />
    <motion.rect x="9" width="6" rx="1.2" animate={{ y: [7, 5.5, 7], height: [14, 15.5, 14] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }} />
    <motion.rect x="17" width="6" rx="1.2" animate={{ y: [15, 14, 15], height: [6, 7, 6] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.45 }} />
  </motion.svg>
);


// Count-up number that animates from 0 to target when scrolled into view.
const AnimatedStat = ({ target, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce || target === 0) { setVal(target); return; }
    let raf;
    const dur = 1200;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, reduce]);
  return <span ref={ref}>{Math.round(val)}{suffix}</span>;
};

const BLUE = "#2563EB";

// ─── Hero phone mockup with autoplaying promo video ─────────────────
// Vertical 9:16 reel plays muted + looped (browser autoplay requirement),
// with a small audio on/off toggle. Realistic device frame, floating.
const HeroPhoneMockup = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const prefersReduced = useReducedMotion();

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) {
      v.play?.().catch(() => {});
    }
    setMuted(next);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: -2 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="relative mx-auto w-[230px] sm:w-[260px] lg:w-[300px]"
      data-testid="hero-phone-mockup"
    >
      {/* Soft glow behind the phone */}
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[3rem] blur-3xl opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 35%, ${ACCENT}, transparent 70%)` }}
      />

      {/* Floating decorative badges */}
      <motion.div
        aria-hidden
        animate={prefersReduced ? undefined : { y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-6 top-16 z-20 hidden sm:flex items-center gap-1.5 rounded-2xl bg-white px-3 py-2 shadow-xl border border-zinc-100"
      >
        <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-[11px] font-black text-zinc-800">0% commissioni</span>
      </motion.div>
      <motion.div
        aria-hidden
        animate={prefersReduced ? undefined : { y: [0, 8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute -right-5 bottom-24 z-20 hidden sm:flex items-center gap-1.5 rounded-2xl bg-white px-3 py-2 shadow-xl border border-zinc-100"
      >
        <Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} />
        <span className="text-[11px] font-black text-zinc-800">Concierge AI</span>
      </motion.div>

      {/* Device frame */}
      <div className="relative z-10 rounded-[2.6rem] bg-zinc-900 p-2.5 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/10">
        <div className="relative overflow-hidden rounded-[2.1rem] bg-black aspect-[440/956]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 h-5 w-24 rounded-full bg-zinc-900" />
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            src="/promo-hero-reel-v2.mp4"
            poster="/promo-hero-reel-v2-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="hero-promo-video"
          />
          {/* Audio toggle */}
          <button
            onClick={toggleSound}
            aria-label={muted ? "Attiva audio" : "Disattiva audio"}
            className="absolute bottom-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
            data-testid="hero-video-sound-toggle"
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Full-bleed promo video between the hero and the yellow box ─────────
// Edge-to-edge, fixed 16:9 ratio → scales down with the viewport width so
// video AND text stay fully visible and proportional on any display.
// Muted autoplay + loop, with an unmute toggle. NO scroll effect.
const PromoVideoSection = ({ src = "/promo-fullpage.mp4", poster = "/promo-fullpage-poster.jpg", showText = true, testId = "promo-video", id }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [inView, setInView] = useState(false);

  // Highlight the word wrapped in {…} (orange), keep the rest white.
  const renderHl = (str) => (str || "").split(/[{}]/).map((part, i) =>
    i % 2 === 1
      ? <span key={i} style={{ color: AMBER }}>{part}</span>
      : <span key={i}>{part}</span>
  );

  // Load + play the video only when the section approaches the viewport,
  // so it never competes with the initial page render.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (inView) videoRef.current?.play?.().catch(() => {});
  }, [inView]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) v.play?.().catch(() => {});
    setMuted(next);
  };

  return (
    <section ref={sectionRef} id={id} className="relative w-full bg-black" style={{ scrollMarginTop: 80 }} data-testid={`${testId}-section`}>
      <div className="relative w-full aspect-video overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={inView ? src : undefined}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          data-testid={testId}
        />
        {showText && (<>
          {/* Gradient for legible text */}
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none" />

          {/* Overlay copy — smaller, scales with the video via vw units */}
          <div className="absolute inset-x-0 bottom-0 px-[4vw] pb-[2.5vw] text-center">
            <h2
              className="text-white font-black tracking-tighter leading-[1.05] drop-shadow-xl"
              style={{ fontSize: "clamp(1rem, 3.1vw, 3rem)" }}
            >
              {renderHl(t("lp.promo_video.title"))}
            </h2>
            <p
              className="mt-[0.8vw] mx-auto max-w-[85%] text-white/90 font-bold leading-snug drop-shadow-lg"
              style={{ fontSize: "clamp(0.5rem, 1.35vw, 1rem)" }}
            >
              {renderHl(t("lp.promo_video.sub"))}
            </p>
          </div>
        </>)}

        {/* Audio toggle */}
        <button
          onClick={toggleSound}
          aria-label={muted ? "Attiva audio" : "Disattiva audio"}
          className="absolute top-[2.5vw] right-[2.5vw] z-20 flex items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
          style={{ width: "clamp(2.25rem, 4vw, 3.25rem)", height: "clamp(2.25rem, 4vw, 3.25rem)" }}
          data-testid={`${testId}-sound-toggle`}
        >
          {muted ? <VolumeX className="w-1/2 h-1/2" /> : <Volume2 className="w-1/2 h-1/2" />}
        </button>
      </div>
    </section>
  );
};


// ─── Animated PWA install glyph ─────────────────────────────────────
// Tiny SVG smartphone that zooms rhythmically to reveal the Hostland app
// icon on the home screen. Used ONLY for the `pwa_install` feature card
// so it visually screams "install me on your phone".
const PWAAppZoomIcon = () => (
  <motion.div
    aria-hidden
    className="relative w-9 h-9"
    initial={{ scale: 0.9 }}
    animate={{ scale: [0.92, 1.08, 0.92] }}
    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
  >
    {/* Phone silhouette */}
    <svg viewBox="0 0 40 40" className="absolute inset-0 w-full h-full">
      <rect x="10" y="3" width="20" height="34" rx="4" fill="#111827" />
      <rect x="12" y="7" width="16" height="24" rx="1.5" fill="#F4F4F5" />
      <circle cx="20" cy="34" r="1.2" fill="#3F3F46" />
    </svg>
    {/* Hostland app icon inside the screen — zoom on top */}
    <motion.div
      className="absolute inset-0 flex items-center justify-center"
      animate={{ scale: [0.85, 1.15, 0.85], y: [1, -1, 1] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="w-4 h-4 rounded-[5px] shadow-lg overflow-hidden flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})` }}>
        <span className="text-white text-[8px] font-black leading-none">H</span>
      </div>
    </motion.div>
    {/* Notification badge pulse */}
    <motion.span
      className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-white"
      animate={{ scale: [1, 1.35, 1], opacity: [1, 0.6, 1] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
    />
  </motion.div>
);

// Icon lookup per key (le features hanno {key, color, title, desc} in landingContent.js)
const FEATURE_ICONS = {
  // Sito Web
  voice: <Mic className="w-5 h-5" />,
  pwa_install: <PWAAppZoomIcon />,
  landing_premium: <Home className="w-5 h-5" />,
  foto_video: <Sparkles className="w-5 h-5" />,
  ical: <Calendar className="w-5 h-5" />,
  booking_engine: <Link2 className="w-5 h-5" />,
  mappa: <MapPin className="w-5 h-5" />,
  meteo: <Sparkles className="w-5 h-5" />,
  google_reviews: <Star className="w-5 h-5" />,
  blog: <Newspaper className="w-5 h-5" />,
  servizi_extra: <ShoppingBag className="w-5 h-5" />,
  contatti: <Phone className="w-5 h-5" />,
  chat: <MessageCircle className="w-5 h-5" />,
  fedelta: <Gift className="w-5 h-5" />,
  lingue: <Globe className="w-5 h-5" />,
  font_colori: <Sparkles className="w-5 h-5" />,
  seo_geo: <Search className="w-5 h-5" />,
  qr: <QrCode className="w-5 h-5" />,
  short_link: <Link2 className="w-5 h-5" />,
  cin: <Shield className="w-5 h-5" />,
  analytics: <BarChart3 className="w-5 h-5" />,
  autosave: <Zap className="w-5 h-5" />,
  // GuestApp
  tours: <Map className="w-5 h-5" />,
  wifi: <Wifi className="w-5 h-5" />,
  video_welcome: <Sparkles className="w-5 h-5" />,
  message_welcome: <MessageCircle className="w-5 h-5" />,
  live_news: <Bell className="w-5 h-5" />,
  translations: <Globe className="w-5 h-5" />,
  concierge: <Compass className="w-5 h-5" />,
  sconti: <BadgePercent className="w-5 h-5" />,
  weather: <Sparkles className="w-5 h-5" />,
  manuals: <BookOpen className="w-5 h-5" />,
  extras: <ShoppingBag className="w-5 h-5" />,
  getyourguide: <Compass className="w-5 h-5" />,
  tour_guide: <Compass className="w-5 h-5" />,
  sitters: <Users className="w-5 h-5" />,
  rentals: <Car className="w-5 h-5" />,
  parking: <MapPin className="w-5 h-5" />,
  transport: <Map className="w-5 h-5" />,
  checkin: <Shield className="w-5 h-5" />,
  waste: <Calendar className="w-5 h-5" />,
  rules: <BookOpen className="w-5 h-5" />,
  numbers: <Phone className="w-5 h-5" />,
  sos: <Phone className="w-5 h-5" />,
  favorites: <Heart className="w-5 h-5" />,
  google_review: <Star className="w-5 h-5" />,
  qr_link: <QrCode className="w-5 h-5" />,
  brand: <Sparkles className="w-5 h-5" />,
  preview: <Zap className="w-5 h-5" />,
};

const mapFeatures = (list, lang) => list.map(f => ({
  icon: FEATURE_ICONS[f.key] || <Sparkles className="w-5 h-5" />,
  title: getT(f.title, lang),
  desc: getT(f.desc, lang),
  color: f.color,
  badge: f.badge ? (typeof f.badge === "string" ? f.badge : getT(f.badge, lang)) : undefined,
}));

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } })
};

// ═══ Mobile performance hook ═══
// Detect small/coarse-pointer devices so we can skip GPU-heavy tricks
// (infinite floaters, scroll parallax, autoplay videos) that were making
// the phone heat up and drop frames. Uses matchMedia so it's cheap and
// reactive to orientation changes.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px), (pointer: coarse)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return isMobile;
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (i = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.45, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } })
};

/* ─── Typewriter Effect (intersection-observer-driven) ─── */
const Typewriter = ({ text, className, style, delay = 0 }) => {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && !started) setStarted(true); }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    setDisplayed("");
    const base = delay * 1000;
    const timers = text.split("").map((_, i) =>
      setTimeout(() => setDisplayed(text.slice(0, i + 1)), base + 30 + i * 40)
    );
    return () => timers.forEach(clearTimeout);
  }, [started, text, delay]);
  return (
    <span ref={ref} className={className} style={style}>
      {displayed || "\u00A0"}
      {started && displayed.length < text.length && (
        <span className="inline-block w-[2px] h-[0.85em] bg-current ml-0.5 align-middle animate-pulse" />
      )}
    </span>
  );
};

/* ─── Audience Toggle (Host / Viaggiatore) — both labels always visible, animated red pill ─── */
const AudienceToggle = ({ audience, setAudience, t }) => (
  <div
    role="tablist"
    aria-label="Scegli il tuo profilo"
    className="relative inline-grid grid-cols-2 p-1 rounded-full bg-white/85 backdrop-blur-xl border border-zinc-200 shadow-sm"
    data-testid="audience-toggle"
  >
    {/* Sliding red pill — CSS transform for smooth animation between the two halves */}
    <span
      aria-hidden
      className="absolute top-1 bottom-1 left-1 rounded-full shadow-md pointer-events-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        width: "calc(50% - 4px)",
        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
        transform: audience === "host" ? "translateX(0%)" : "translateX(100%)",
      }}
    />
    <button
      role="tab"
      aria-selected={audience === "host"}
      onClick={() => setAudience("host")}
      className={`relative z-10 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-black transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${audience === "host" ? "text-white" : "text-zinc-700 hover:text-zinc-900"}`}
      data-testid="toggle-host"
    >
      <Home className="w-3.5 h-3.5" /> {t ? t("lp.toggle.host") : "Host / PM"}
    </button>
    <button
      role="tab"
      aria-selected={audience === "guest"}
      onClick={() => setAudience("guest")}
      className={`relative z-10 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-black transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${audience === "guest" ? "text-white" : "text-zinc-700 hover:text-zinc-900"}`}
      data-testid="toggle-guest"
    >
      <Plane className="w-3.5 h-3.5" /> {t ? t("lp.toggle.guest") : "Viaggiatore"}
    </button>
  </div>
);

/* ─── Big Home Search Bar (Airbnb-inspired) ─── */
const HomeSearch = ({ navigate, compact = false, t }) => {
  const [open, setOpen] = useState(!compact);
  const [comune, setComune] = useState(null);
  const [guests, setGuests] = useState(2);
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");

  const submit = () => {
    const params = new URLSearchParams();
    if (comune?.istat) params.set("istat", comune.istat);
    if (guests > 0) params.set("guests", String(guests));
    if (checkin) params.set("checkin", checkin);
    if (checkout) params.set("checkout", checkout);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="max-w-3xl mx-auto"
      data-testid="home-search"
    >
      {compact ? (
        <motion.button
          onClick={() => setOpen((v) => !v)}
          whileTap={{ scale: 0.99 }}
          className="group w-full flex items-center gap-3 bg-white border-2 border-zinc-200 hover:border-zinc-900 rounded-full px-5 py-4 shadow-lg shadow-zinc-900/5 hover:shadow-2xl transition-all"
          data-testid="home-search-trigger"
        >
          <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white" style={{ background: ACCENT }}>
            <Search className="w-4 h-4" />
          </span>
          <span className="flex-1 text-left">
            <span className="block text-sm font-bold text-zinc-900 leading-tight">Cerca la tua prossima destinazione</span>
            <span className="block text-[11px] text-zinc-500 leading-tight mt-0.5">Comune · Ospiti · Date</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </motion.button>
      ) : null}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className={`bg-white border border-zinc-200 rounded-3xl shadow-2xl p-4 md:p-5 ${compact ? "mt-3" : ""} text-left`} data-testid="home-search-panel">
              <div className="grid grid-cols-1 md:grid-cols-[1.6fr_auto_auto_auto] gap-3 items-end">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">{t ? t("lp.search.destination_label") : "Dove vuoi andare?"}</label>
                  <ComuneAutocomplete value={comune} onChange={setComune} placeholder={t ? t("lp.search.destination_ph") : "Es. Milano, Roma, Firenze..."} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">{t ? t("lp.search.guests") : "Ospiti"}</label>
                  <div className="flex items-center bg-white border border-zinc-200 rounded-2xl px-3 py-3 min-w-[110px]">
                    <Users className="w-4 h-4 text-zinc-400 mr-2" />
                    <input type="number" min={1} value={guests} onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                      className="w-12 text-sm font-bold focus:outline-none" data-testid="home-search-guests" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">{t ? t("lp.search.checkin") : "Check-in"}</label>
                  <input type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)}
                    className="bg-white border border-zinc-200 rounded-2xl px-3 py-3 text-sm font-semibold focus:outline-none"
                    data-testid="home-search-checkin" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">{t ? t("lp.search.checkout") : "Check-out"}</label>
                  <input type="date" value={checkout} onChange={(e) => setCheckout(e.target.value)}
                    className="bg-white border border-zinc-200 rounded-2xl px-3 py-3 text-sm font-semibold focus:outline-none"
                    data-testid="home-search-checkout" />
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.97 }}
                whileHover={{ scale: 1.01 }}
                onClick={submit}
                className="w-full mt-4 text-white rounded-2xl py-3.5 font-bold text-sm flex items-center justify-center gap-2 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)` }}
                data-testid="home-search-submit"
              >
                <Search className="w-4 h-4" /> {t ? t("lp.search.submit") : "Cerca alloggi"}
              </motion.button>
              <p className="text-[10px] text-zinc-400 mt-2 text-center font-medium">
                {t ? t("lp.search.note") : "Zero commissioni · contatto diretto con l'host"}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── Infinite Slider (properties, Apple App Store carousel-style) ─── */
const InfiniteSlider = ({ items, speed = 0.35, trackClick = false }) => {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  const duped = [...items, ...items, ...items];

  useEffect(() => {
    const el = ref.current;
    if (!el || items.length === 0) return;
    let raf, pos = 0;
    const cardW = 240 + 16;
    const totalW = items.length * cardW;
    const tick = () => {
      if (!paused) { pos += speed; if (pos >= totalW) pos -= totalW; el.style.transform = `translateX(-${pos}px)`; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [items.length, paused, speed]);

  const handleClick = (pid) => {
    if (!trackClick) return;
    fetch(`${API}/properties/${pid}/track-click`, { method: "POST" }).catch(() => {});
  };

  if (items.length === 0) return null;
  return (
    <div className="overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="property-slider">
      <div ref={ref} className="flex gap-4 will-change-transform" style={{ width: "max-content" }}>
        {duped.map((p, i) => {
          const cover = p.cover || (p.media?.[0] ? `${API}/files/${p.media[0]}` : null);
          const src = cover ? (cover.startsWith("http") ? cover : (cover.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : cover)) : null;
          return (
            <a
              key={`${p.property_id}-${i}`}
              href={`/property/${p.property_id}`}
              onClick={() => handleClick(p.property_id)}
              className="shrink-0 w-[240px] group"
              data-testid={`slider-card-${i}`}
            >
              <div className="relative w-[240px] h-[240px] rounded-2xl overflow-hidden bg-zinc-200 border border-zinc-200 shadow-sm group-hover:shadow-xl transition-all duration-500">
                {src ? (
                  <img src={src} alt={p.name ? `${p.name} — casa vacanze su Hostland` : "Casa vacanze su Hostland"} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Home className="w-8 h-8 text-zinc-300" /></div>
                )}
                <div aria-hidden className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                {p.vdh_chosen && (
                  <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-white bg-red-600 px-1.5 py-0.5 rounded-full shadow">
                    <Flame className="w-2.5 h-2.5" /> VDH
                  </span>
                )}
                {trackClick && (p.clicks_count || 0) > 0 && (
                  <span
                    className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-black/55 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-sm"
                    title={`${p.clicks_count} visualizzazioni`}
                    data-testid={`slider-card-clicks-${i}`}
                  >
                    <Eye className="w-3 h-3" />
                    {p.clicks_count >= 1000 ? `${(p.clicks_count / 1000).toFixed(1)}k` : p.clicks_count}
                  </span>
                )}
                {p?.pricing?.enabled && p?.pricing?.base_price > 0 && (
                  <span
                    className="absolute bottom-14 right-2.5 inline-flex items-baseline gap-1 rounded-full px-2.5 py-1 bg-white/95 backdrop-blur-sm shadow-md"
                    data-testid={`slider-card-price-${i}`}
                  >
                    <span className="text-[8px] font-black uppercase tracking-widest text-zinc-500">Da</span>
                    <span className="text-xs font-black text-zinc-900">€{Math.round(p.pricing.base_price).toLocaleString("it-IT")}</span>
                    <span className="text-[8px] font-medium text-zinc-500">/notte</span>
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <p className="text-sm font-black truncate leading-tight" title={p.name}>{p.name || "—"}</p>
                  <p className="text-[11px] opacity-90 truncate mt-0.5">{p.address || p.comune_name || ""}</p>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

/* ─── FAQ Accordion Item ─── */
const FAQItem = ({ question, answer, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div variants={fadeUp} custom={index} className="border border-zinc-200/70 rounded-2xl overflow-hidden bg-white/70 backdrop-blur-sm" data-testid={`faq-item-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 hover:bg-zinc-50/60 transition-colors">
        <span className="font-bold text-zinc-900 text-sm">{question}</span>
        <ChevronRight className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <p className="px-5 pb-4 text-sm text-zinc-500 font-medium leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── Apple-style Product Showcase Section ───
    Sticky phone mockup on one side, features stagger-in on the other.
    Parallax on scroll, subtle rotation on Y axis, glass background. */
const PreCheckinBannerCompact = ({ onCta }) => (
  <div className="w-full max-w-4xl mx-auto mt-5 rounded-2xl relative overflow-hidden" style={{ background: "#17120f" }} data-testid="precheckin-banner-compact">
    <div aria-hidden className="absolute -top-16 -right-10 w-44 h-44 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #D7362B, transparent 70%)" }} />
    <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 sm:p-6">
      <span className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shrink-0 shadow-lg"><ShieldCheck className="w-6 h-6" /></span>
      <div className="flex-1 min-w-0 text-left">
        <span className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-red-300"><Sparkles className="w-3 h-3" /> Novità · Hostland CONTROL</span>
        <h3 className="text-lg font-black text-white tracking-tight mt-1 leading-snug">Check-in online e invio automatico ad Alloggiati Web e ROSS 1000</h3>
        <p className="text-zinc-400 text-xs font-medium mt-1">Documenti ospiti → <b className="text-zinc-200">Alloggiati Web</b> e <b className="text-zinc-200">ROSS 1000</b>, in automatico. 19,90 €/anno.</p>
      </div>
      <button onClick={onCta} className="shrink-0 rounded-full px-5 py-2.5 font-black text-xs text-white shadow-lg inline-flex items-center gap-2 w-full sm:w-auto justify-center" style={{ background: "linear-gradient(135deg, #D7362B, #991b1b)" }} data-testid="precheckin-compact-cta">
        <ShieldCheck className="w-3.5 h-3.5" /> Scopri <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

const ProductShowcase = ({
  side = "right",           // phone side (right | left)
  eyebrow,
  eyebrowColor = ACCENT,
  eyebrowBg = `${ACCENT}10`,
  title,
  titleGradient,
  subtitle,
  video,
  poster,
  iframeSrc,                // NEW — when set, render a live iframe of the demo
  iframeTitle,
  bg = "linear-gradient(180deg, #FFF8ED 0%, #FFFFFF 40%, #FFF1F4 100%)",
  glow1 = `${AMBER}55`,
  glow2 = `${ACCENT}55`,
  features,
  ctaLabel,
  onCta,
  testid,
  floaters = [],
  liveDemoLink,
  liveDemoLabel,
  beforeCta,
}) => {
  const sectionRef = useRef(null);
  const phoneRef = useRef(null);
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();
  // Lazy-load the iframe only when the phone mockup enters the viewport.
  // Two full app iframes at page load would tank first paint on the landing.
  const [iframeReady, setIframeReady] = useState(false);
  useEffect(() => {
    if (!iframeSrc || !phoneRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setIframeReady(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(phoneRef.current);
    return () => io.disconnect();
  }, [iframeSrc]);

  // Render the live demo iframe at a REAL smartphone logical width (390px) and
  // scale it down to fit the mockup screen. Otherwise the iframe adopts the
  // tiny frame width (~280px on mobile) and the app squishes / overflows.
  const screenRef = useRef(null);
  const DEVICE_W = 390;
  const DEVICE_H = Math.round((DEVICE_W * 956) / 440); // match frame aspect ratio
  const [scale, setScale] = useState(0);
  useEffect(() => {
    if (!iframeSrc) return;
    const el = screenRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / DEVICE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [iframeSrc, iframeReady]);
  // Static, non-animated tilt. Users can freely scroll the demo iframe
  // without a moving mockup fighting for their attention. Slight rotation
  // (opposite direction per section) plus a soft drop shadow so it still
  // feels editorial, not clinical.
  const staticTilt = reduce ? 0 : (side === "right" ? -2.4 : 2.4);

  return (
    <section ref={sectionRef} id={testid} className="px-4 sm:px-6 py-12 md:py-20 relative overflow-hidden" style={{ background: bg }}>
      {/* Static background glow blobs — no animation, per user request */}
      <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-2xl opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${glow1}, transparent 70%)` }} />
      <div aria-hidden className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full blur-2xl opacity-20 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${glow2}, transparent 70%)` }} />
      <motion.div
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
        className="max-w-6xl mx-auto relative"
      >
        <motion.div variants={fadeUp} className="text-center mb-10 md:mb-14">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4" style={{ background: eyebrowBg, color: eyebrowColor }}>
            {eyebrow}
          </span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-zinc-900 tracking-tighter mb-3 leading-[1.02]">
            {title}{" "}
            {titleGradient && (
              <span style={{ backgroundImage: `linear-gradient(105deg, ${eyebrowColor}, ${AMBER})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {titleGradient}
              </span>
            )}
          </h2>
          <p className="text-zinc-500 font-medium text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </motion.div>

        {/* ═══ Demo live interattiva — protagonista della sezione ═══ */}
        <div className="flex flex-col items-center">
          {/* Barra tattile — invito diretto a toccare la vera app */}
          <motion.div variants={fadeUp} className="mb-5 md:mb-7">
            <span className="inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full bg-zinc-900 text-white text-[12px] sm:text-[13px] font-bold shadow-xl shadow-black/25 border border-white/10">
              <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              <span className="tracking-tight">Provala qui — usala come se fosse tua</span>
            </span>
          </motion.div>

          {/* Phone mockup — grande, centrato, frame titanio premium.
              L'iframe è la vera app, completamente scrollabile e toccabile. */}
          <motion.div
            variants={scaleIn}
            ref={phoneRef}
            className="relative mx-auto w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[440px] xl:max-w-[460px]"
            data-testid={`${testid}-phone`}
          >
            <div aria-hidden className="absolute inset-0 -m-10 rounded-[60px] blur-3xl opacity-50 pointer-events-none"
              style={{ background: `radial-gradient(circle at 50% 45%, ${eyebrowColor}45, transparent 62%)` }} />

            <div
              className="relative mx-auto"
              style={{
                aspectRatio: "440/956",
                transform: `rotate(${staticTilt}deg)`,
                transformOrigin: "center 60%",
              }}
            >
              {/* chassis titanio */}
              <div
                className="absolute inset-0 rounded-[52px] p-[11px] sm:p-[13px]"
                style={{
                  background: "linear-gradient(150deg,#42424a 0%,#0a0a0c 46%,#2a2a30 100%)",
                  boxShadow: `0 40px 90px -20px ${eyebrowColor}40, 0 60px 150px -55px rgba(0,0,0,0.65), inset 0 1.5px 0 rgba(255,255,255,0.16), inset 0 0 0 1.5px rgba(255,255,255,0.05)`,
                }}
              >
                {/* pulsanti laterali */}
                <div aria-hidden className="absolute -left-[3px] top-[22%] w-[3px] h-9 rounded-l bg-zinc-700/90" />
                <div aria-hidden className="absolute -left-[3px] top-[33%] w-[3px] h-14 rounded-l bg-zinc-700/90" />
                <div aria-hidden className="absolute -right-[3px] top-[27%] w-[3px] h-20 rounded-r bg-zinc-700/90" />

                <div ref={screenRef} className="relative w-full h-full rounded-[42px] overflow-hidden bg-black">
                  {/* dynamic island */}
                  <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-[30%] h-6 rounded-full bg-black flex items-center justify-end px-2.5 shadow-inner">
                    <span className="w-2 h-2 rounded-full bg-zinc-900 ring-1 ring-zinc-700/70" />
                  </div>
                  {iframeSrc ? (
                    <>
                      {!(iframeReady && scale > 0) && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-zinc-50 to-white">
                          <div className="w-7 h-7 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
                          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">Demo live</span>
                        </div>
                      )}
                      {iframeReady && scale > 0 && (
                        <div
                          className="absolute top-0 left-0"
                          style={{
                            width: DEVICE_W,
                            height: DEVICE_H,
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                          }}
                        >
                          <iframe
                            src={iframeSrc}
                            title={iframeTitle || "Live demo"}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                            className="border-0 bg-white block"
                            style={{ width: DEVICE_W, height: DEVICE_H }}
                            data-testid={`${testid}-live-iframe`}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-950 text-white">
                      <Home className="w-10 h-10 opacity-30" />
                    </div>
                  )}
                  {/* riflesso vetro */}
                  <div aria-hidden className="absolute inset-0 pointer-events-none z-20"
                    style={{ background: "linear-gradient(120deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 26%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.06) 100%)" }} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA live sotto il telefono */}
          {liveDemoLink && (
            <motion.div variants={fadeUp} className="mt-7 md:mt-9 flex flex-col items-center gap-2">
              <motion.a
                href={liveDemoLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                className="group relative inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-black text-[15px] text-white shadow-[0_16px_36px_-10px_rgba(0,0,0,0.4)] overflow-hidden"
                style={{ background: `linear-gradient(105deg, ${eyebrowColor} 0%, ${ACCENT_DARK} 55%, ${AMBER} 100%)`, backgroundSize: "200% auto" }}
                data-testid={`${testid}-live-demo-btn`}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                <span className="tracking-tight">{liveDemoLabel || "Guarda in live"}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </motion.a>
              <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Nessuna registrazione · scorri e tocca · è la vera app</p>
            </motion.div>
          )}

          {/* Feature cards di supporto — secondarie rispetto alla demo */}
          <div className="w-full max-w-4xl mt-10 md:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {features.map((f, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                custom={i}
                whileHover={{ y: -3, scale: 1.02 }}
                className="relative p-4 rounded-2xl bg-white/95 backdrop-blur-sm border border-zinc-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
                data-testid={`${testid}-feature-${i}`}
              >
                <div aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: `linear-gradient(135deg, ${f.color}08, transparent 60%)` }} />
                {f.badge && (
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: [1, 1.06, 1], opacity: 1 }}
                    transition={{ scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.4 } }}
                    className="absolute top-2 right-2 text-[8.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full text-white shadow"
                    style={{ background: `linear-gradient(135deg, ${f.color}, ${AMBER})` }}
                  >
                    {f.badge}
                  </motion.span>
                )}
                <div className="relative">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: `${f.color}15`, color: f.color }}>
                    {f.icon}
                  </div>
                  <div className="text-[12.5px] font-black text-zinc-900 mb-0.5 leading-tight">{f.title}</div>
                  <p className="text-[11px] text-zinc-500 font-medium leading-snug">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {beforeCta}
        </div>

        <motion.div variants={fadeUp} className="mt-12 md:mt-16 text-center">
          <motion.button
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={onCta}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-black text-sm shadow-xl"
            style={{ background: `linear-gradient(105deg, ${eyebrowColor} 0%, ${ACCENT_DARK} 50%, ${AMBER} 100%)`, backgroundSize: "200% auto" }}
            data-testid={`${testid}-cta`}
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </motion.button>
          <p className="text-[11px] text-zinc-400 mt-3 font-semibold">30 giorni gratis · pagamenti via Stripe<sup className="text-[7px]">®</sup></p>
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ─── Animated CountUp — smooth number ramp with easing ─── */
const CountUp = ({ end = 0, duration = 1400, prefix = "", suffix = "" }) => {
  const [value, setValue] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!end || started.current) { setValue(end); return; }
    started.current = true;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutExpo
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setValue(Math.floor(eased * end));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return <>{prefix}{value.toLocaleString("it-IT")}{suffix}</>;
};

/* ─── Big rotating word (Apple-style hero flavor) ─── */
const RotatingWord = ({ words, className, style }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % words.length), 2400);
    return () => clearInterval(t);
  }, [words.length]);
  return (
    <span className={`inline-block relative ${className || ""}`} style={{ minWidth: "1ch", whiteSpace: "nowrap", ...style }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ display: "inline-block", background: `linear-gradient(105deg, ${ACCENT}, ${AMBER}, ${ACCENT})`, backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════ */

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();
  const heavyFx = !(isMobile || reduce); // desktop-only intensive animations
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Persist audience across visits so returning users don't see the wrong hero flicker.
  const [audience, setAudience] = useState(() => {
    try { return localStorage.getItem("hostland_audience") || "host"; } catch { return "host"; }
  });
  useEffect(() => { try { localStorage.setItem("hostland_audience", audience); } catch {} }, [audience]);

  const [mostClicked, setMostClicked] = useState([]);
  const [latestProps, setLatestProps] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [cityCounts, setCityCounts] = useState({});

  // Top 10 città italiane con foto Wikipedia (Special:FilePath — HTTP 200 verificati manualmente).
  // Ogni URL punta a un file esistente nel commons Wikipedia, servito con ridimensionamento automatico via `?width=800`.
  const TOP_CITIES = [
    { name: "Roma",     istat: "058091", img: "https://en.wikipedia.org/wiki/Special:FilePath/Colosseo_2020.jpg?width=800" },
    { name: "Milano",   istat: "015146", img: "https://en.wikipedia.org/wiki/Special:FilePath/Milano_Duomo.jpg?width=800" },
    { name: "Firenze",  istat: "048017", img: "https://en.wikipedia.org/wiki/Special:FilePath/Florence_Duomo.jpg?width=800" },
    { name: "Venezia",  istat: "027042", img: "https://en.wikipedia.org/wiki/Special:FilePath/Venice_by_night.jpg?width=800" },
    { name: "Napoli",   istat: "063049", img: "https://en.wikipedia.org/wiki/Special:FilePath/Napoli_e_Vesuvio.jpg?width=800" },
    { name: "Bologna",  istat: "037006", img: "https://en.wikipedia.org/wiki/Special:FilePath/Bologna_-_Piazza_Maggiore.jpg?width=800" },
    { name: "Torino",   istat: "001272", img: "https://en.wikipedia.org/wiki/Special:FilePath/Mole_Antonelliana_(Torino).jpg?width=800" },
    { name: "Verona",   istat: "023091", img: "https://en.wikipedia.org/wiki/Special:FilePath/Arena_di_Verona.jpg?width=800" },
    { name: "Palermo",  istat: "082053", img: "https://en.wikipedia.org/wiki/Special:FilePath/Teatro_Massimo_Palermo.jpg?width=800" },
    { name: "Genova",   istat: "010025", img: "https://en.wikipedia.org/wiki/Special:FilePath/Panorama_di_Genova.jpg?width=800" },
  ];

  useEffect(() => {
    axios.get(`${API}/properties/featured/most-clicked?limit=20`).then(r => setMostClicked(r.data.items || [])).catch(() => {});
    axios.get(`${API}/properties/featured/latest?limit=20`).then(r => setLatestProps(r.data.items || [])).catch(() => {});
    axios.get(`${API}/events/featured?limit=12`).then(r => setFeaturedEvents(r.data.items || [])).catch(() => {});
    axios.get(`${API}/leaderboard/stats`).then(r => setTotalProperties(r.data.total_properties || 0)).catch(() => {});
    // Fetch city counts one-shot (aggregate MongoDB) — solo top 10 città italiane
    const cityList = "Roma,Milano,Firenze,Venezia,Napoli,Bologna,Torino,Verona,Palermo,Genova";
    axios.get(`${API}/cities/counts?cities=${encodeURIComponent(cityList)}`).then(r => setCityCounts(r.data.counts || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const hash = (window.location.hash || "").slice(1);
    if (!hash) { window.scrollTo({ top: 0, behavior: "instant" }); return; }
    // Sections below the hero mount after data loads; retry until the target exists.
    let tries = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) { el.scrollIntoView({ behavior: "instant", block: "start" }); }
      else if (tries++ < 25) { timer = setTimeout(tryScroll, 120); }
    };
    tryScroll();
    return () => clearTimeout(timer);
  }, []);

  // Scroll progress for the hero (Apple-style intro parallax)
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroTitleY = useTransform(heroProgress, [0, 1], [0, -80]);
  const heroTitleOpacity = useTransform(heroProgress, [0, 0.8], [1, 0]);

  return (
    <div className="landing-home min-h-screen bg-[#F5F5F7] overflow-hidden">
      <SitePopup />
      <Helmet>
        <title>Sito per case vacanze e B&B con Welcomebook | Hostland</title>
        <meta name="description" content="Sito per case vacanze e B&B senza commissioni e Welcomebook digitale in 5 lingue, con invio ad Alloggiati Web e ISTAT. Prova gratis 30 giorni." />
        <link rel="canonical" href="https://hostland.vitadahost.it/" />
        <link rel="alternate" hrefLang="it-IT" href="https://hostland.vitadahost.it/" />
        <link rel="alternate" hrefLang="x-default" href="https://hostland.vitadahost.it/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Sito per case vacanze e B&B con Welcomebook | Hostland" />
        <meta property="og:description" content="Sito per case vacanze e B&B senza commissioni e Welcomebook digitale in 5 lingue, con invio ad Alloggiati Web e ISTAT. Prova gratis 30 giorni." />
        <meta property="og:url" content="https://hostland.vitadahost.it/" />
        <meta property="og:image" content="https://hostland.vitadahost.it/hostland-social.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Hostland, sito e Welcomebook digitale per case vacanze e B&B" />
        <meta property="og:site_name" content="Hostland by Vita da Host" />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:locale:alternate" content="fr_FR" />
        <meta property="og:locale:alternate" content="de_DE" />
        <meta property="og:locale:alternate" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sito per case vacanze e B&B con Welcomebook | Hostland" />
        <meta name="twitter:description" content="Sito per case vacanze e B&B senza commissioni e Welcomebook digitale in 5 lingue, con invio ad Alloggiati Web e ISTAT. Prova gratis 30 giorni." />
        <meta name="twitter:image" content="https://hostland.vitadahost.it/hostland-social.png" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="theme-color" content="#141414" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              "name": "Hostland",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "url": "https://hostland.vitadahost.it/",
              "description": "Sito per case vacanze e B&B senza commissioni e Welcomebook digitale per ospiti",
              "offers": { "@type": "Offer", "price": "59.90", "priceCurrency": "EUR" },
              "publisher": { "@type": "Organization", "name": "Vita da Host", "url": "https://www.vitadahost.it/" }
            },
            {
              "@type": "WebSite",
              "name": "Hostland",
              "url": "https://hostland.vitadahost.it/",
              "inLanguage": "it-IT",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://hostland.vitadahost.it/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        })}</script>
      </Helmet>

      <HomeHero />

      {/* ═══ PROMO VIDEO — full-bleed, responsive (no scroll effect) ═══ */}
      <div className="h-10 md:h-16" aria-hidden="true" />
      <PromoVideoSection showText={false} id="video-sito" />

      {/* ═══ TOP 10 CITTÀ ITALIANE — rimossa dalla home (vive su /prenotasenzacommissioni) ═══ */}
      <AnimatePresence>
        {false && (
          <motion.section
            key="cities-section"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
            data-testid="top-cities-section"
          >
            <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
              <motion.div
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
              >
                <motion.div variants={fadeUp} className="text-center mb-10">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4" style={{ background: `${ACCENT}10`, color: ACCENT }}>
                    <MapPin className="w-3.5 h-3.5" /> Le 10 città più amate d'Italia
                  </span>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-zinc-900 tracking-tighter mb-3 leading-[1.02]">
                    Dove vuoi <span style={{ backgroundImage: `linear-gradient(105deg, ${ACCENT}, ${AMBER})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>andare</span>?
                  </h2>
                  <p className="text-zinc-500 font-medium text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                    Scegli la tua prossima destinazione. Contatto diretto con l'host, zero commissioni.
                  </p>
                </motion.div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {TOP_CITIES.map((c, i) => {
                    const count = cityCounts[c.name] ?? null;
                    return (
                      <motion.a
                        key={c.name}
                        variants={scaleIn}
                        custom={i}
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        href={`/search?istat=${c.istat}`}
                        className="relative aspect-[4/5] rounded-2xl overflow-hidden group shadow-md hover:shadow-2xl transition-shadow"
                        data-testid={`top-city-${c.name.toLowerCase()}`}
                      >
                        <img
                          src={c.img}
                          alt={`Alloggi a ${c.name}`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          onError={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #1f2937, #0f172a)"; e.currentTarget.style.display = "none"; }}
                        />
                        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-white">
                          <p className="text-lg md:text-xl font-black tracking-tight leading-tight">{c.name}</p>
                          {count !== null ? (
                            <p className="text-[11px] md:text-xs font-bold opacity-90 mt-0.5 inline-flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              <CountUp end={count} duration={1200} /> {count === 1 ? "struttura" : "strutture"}
                            </p>
                          ) : (
                            <p className="text-[11px] md:text-xs font-bold opacity-70 mt-0.5">Scopri</p>
                          )}
                        </div>
                        <div aria-hidden className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowRight className="w-4 h-4 text-zinc-900" />
                        </div>
                      </motion.a>
                    );
                  })}
                </div>

                <motion.div variants={fadeUp} className="text-center mt-8">
                  <button
                    onClick={() => navigate("/map")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-black text-sm transition-all"
                    data-testid="cities-see-all"
                  >
                    <Map className="w-4 h-4" /> Esplora tutta la mappa
                  </button>
                </motion.div>
              </motion.div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ═══ SECTION 1 — SITO WEB ═══ */}
      <ProductShowcase
        testid="sito"
        eyebrow={<><Home className="w-3 h-3" /> {t("lp.site.eyebrow")}</>}
        eyebrowColor={AMBER}
        eyebrowBg="#F59E0B15"
        title={t("lp.site.title")}
        titleGradient={t("lp.site.title_grad")}
        subtitle={t("lp.site.sub")}
        video="/assets/site-demo.mp4"
        poster="/assets/site-demo-poster.jpg"
        iframeSrc="/property/prop_demo_hostland?embed=1"
        iframeTitle="Demo Landing Property Hostland"
        bg="linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 35%, #FFF1F4 100%)"
        glow1={`${AMBER}55`}
        glow2={`${ACCENT}55`}
        side="right"
        features={mapFeatures(siteFeatures, lang)}
        floaters={[
          { icon: <Calendar className="w-3 h-3" />, label: "Sync iCal", color: "#B45309", pos: "top-1/4 -right-6" },
          { icon: <Search className="w-3 h-3" />, label: "SEO + GEO", color: ACCENT, pos: "bottom-1/4 -left-6" },
        ]}
        ctaLabel={<><Home className="w-4 h-4" /> {t("lp.site.cta")}</>}
        onCta={() => navigate(user ? "/dashboard" : "/login?mode=signup")}
        liveDemoLink="/live/site"
        liveDemoLabel="Guarda la Landing in live"
      />

      {/* ═══ VIDEO WELCOME BOOK — full-bleed, senza testo, responsive ═══ */}
      <PromoVideoSection src="/promo-welcomebook.mp4" poster="/promo-welcomebook-poster.jpg" showText={false} testId="wb-video" id="video-welcomebook" />

      {/* ═══ SECTION 2 — GUESTAPP ═══ */}
      <ProductShowcase
        testid="guestapp"
        eyebrow={<><Sparkles className="w-3 h-3" /> {t("lp.gb.eyebrow")}</>}
        eyebrowColor={ACCENT}
        eyebrowBg={`${ACCENT}12`}
        title={t("lp.gb.title")}
        titleGradient={t("lp.gb.title_grad")}
        subtitle={t("lp.gb.sub")}
        video="/assets/gb-demo.mp4"
        poster="/assets/gb-demo-poster.jpg"
        iframeSrc="/guestbook-v3/prop_demo_hostland?embed=1&showcase=1"
        iframeTitle="Demo Welcome Book Hostland"
        bg="linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 40%, #FEF3C7 100%)"
        glow1={`${ACCENT}55`}
        glow2={`${AMBER}55`}
        side="left"
        features={mapFeatures(guestAppFeatures, lang)}
        floaters={[
          { icon: <Wifi className="w-3 h-3" />, label: "Wi-Fi 1-tap", color: ACCENT, pos: "top-1/3 -left-6" },
          { icon: <Globe className="w-3 h-3" />, label: "5 lingue", color: ACCENT, pos: "bottom-1/3 -right-6" },
        ]}
        ctaLabel={<><Sparkles className="w-4 h-4" /> {t("lp.gb.cta")}</>}
        onCta={() => navigate(user ? "/dashboard" : "/login?mode=signup")}
        beforeCta={
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="pt-6 pb-2 w-full" data-testid="pacchetto-completo-section">
            <div className="max-w-5xl mx-auto rounded-[32px] relative overflow-hidden" style={{ background: "linear-gradient(145deg, #1a1210 0%, #130d1c 55%, #0d1322 100%)" }}>
              <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, white 1px, transparent 1px), radial-gradient(circle at 82% 68%, white 1px, transparent 1px)", backgroundSize: "46px 46px" }} />
              <div aria-hidden className="absolute -top-28 -left-20 w-80 h-80 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: `radial-gradient(circle, ${ACCENT}, transparent 70%)` }} />
              <div aria-hidden className="absolute -bottom-28 -right-16 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: `radial-gradient(circle, ${AMBER}, transparent 70%)` }} />

              <div className="relative z-10 p-8 md:p-12">
                <div className="text-center max-w-2xl mx-auto">
                  <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ background: `${AMBER}1f`, border: `1px solid ${AMBER}44`, color: "#FCD572" }} data-testid="pacchetto-badge">
                    <Sparkles className="w-3.5 h-3.5" /> Tutto integrato nel Welcome Book
                  </span>
                  <h2 className="text-3xl md:text-[2.6rem] font-black text-white tracking-tighter mt-4 leading-[1.05]">
                    Il pacchetto completo per host
                  </h2>
                  <p className="text-zinc-300 text-sm md:text-base font-medium mt-3">
                    Registrazione documenti, invio automatico alla Questura e richiesta dell'imposta di soggiorno: tutto direttamente dal tuo Welcome Book, con il plugin <b className="text-white">DeVisu</b> come aggiunta per l'identificazione in videochiamata.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-9 max-w-3xl mx-auto">
                  {[
                    { icon: <FileText className="w-5 h-5" />, t: "Registrazione documenti / pre check-in", d: "Chiedi i documenti via link prima dell'arrivo." },
                    { icon: <Send className="w-5 h-5" />, t: "Invio automatico ROSS 1000 + Alloggiati Web", d: "Trasmissione alla Questura senza stress, con notifiche." },
                    { icon: <Download className="w-5 h-5" />, t: "Scarico schedine automatico", d: "Ricevute e schedine pronte da scaricare in PDF." },
                    { icon: <CreditCard className="w-5 h-5" />, t: "Imposta di soggiorno via Stripe", d: "L'ospite paga online, l'incasso arriva sul tuo conto." },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-2xl p-4 bg-white/[0.05] border border-white/10" data-testid={`pacchetto-feat-${i}`}>
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${ACCENT}22`, color: "#F7A8BC" }}>{f.icon}</span>
                      <div>
                        <div className="text-white font-black text-sm leading-snug">{f.t}</div>
                        <div className="text-zinc-400 text-xs font-medium mt-0.5 leading-relaxed">{f.d}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 max-w-3xl mx-auto rounded-2xl p-4 bg-white/[0.04] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="pacchetto-pagamenti">
                  <div className="flex items-center gap-2 text-zinc-200 text-xs font-bold">
                    <Wallet className="w-4 h-4" style={{ color: "#FCD572" }} /> Imposta pagabile con:
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {["Apple Pay", "Google Pay", "PayPal", "Carta di credito"].map((p) => (
                      <span key={p} className="rounded-full bg-white text-zinc-900 text-[11px] font-black px-3 py-1.5 shadow-sm">{p}</span>
                    ))}
                  </div>
                </div>
                <p className="text-center text-[11px] text-zinc-500 font-medium mt-2">Commissione Stripe prevista sui pagamenti dell'imposta.</p>

                <div className="grid sm:grid-cols-2 gap-4 mt-8 max-w-3xl mx-auto">
                  <div className="rounded-3xl p-5 border relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(215,54,43,0.14), rgba(255,255,255,0.03))", borderColor: "rgba(247,168,188,0.3)" }} data-testid="pacchetto-prezzo">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: "#F7A8BC" }}><ShieldCheck className="w-3.5 h-3.5" /> Pacchetto check-in + imposta</span>
                    <div className="flex items-baseline gap-1.5 mt-2">
                      <span className="text-3xl font-black text-white">19,90&nbsp;€</span>
                      <span className="text-xs font-bold text-zinc-400">/ anno</span>
                    </div>
                    <p className="text-zinc-400 text-xs font-medium mt-1">Documenti, Alloggiati, ROSS 1000, schedine e imposta di soggiorno.</p>
                    <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.03 }} onClick={() => navigate(user ? "/dashboard" : "/login?as=host")}
                      className="mt-4 w-full rounded-full px-5 py-2.5 font-black text-sm text-white shadow-lg inline-flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg, #D7362B, #991b1b)" }} data-testid="pacchetto-cta-btn">
                      Attiva il pacchetto <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                  <div className="rounded-3xl p-5 border relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(47,111,224,0.16), rgba(255,255,255,0.03))", borderColor: "rgba(96,165,250,0.3)" }} data-testid="pacchetto-devisu-prezzo">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: "#93B4F5" }}><Video className="w-3.5 h-3.5" /> Plugin aggiuntivo · DeVisu</span>
                    <div className="flex items-baseline gap-1.5 mt-2">
                      <span className="text-3xl font-black text-white">+29,90&nbsp;€</span>
                      <span className="text-xs font-bold text-zinc-400">/ anno</span>
                    </div>
                    <p className="text-zinc-400 text-xs font-medium mt-1">Identificazione in videochiamata, conforme alla sentenza del Consiglio di Stato.</p>
                    <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.03 }} onClick={() => navigate(user ? "/dashboard/devisu" : "/login?as=host")}
                      className="mt-4 w-full rounded-full px-5 py-2.5 font-black text-sm text-white shadow-lg inline-flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg, #2F6FE0, #1E40AF)" }} data-testid="pacchetto-devisu-cta-btn">
                      Aggiungi DeVisu <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
                <p className="text-center text-[11px] md:text-xs text-zinc-400 font-medium mt-6 max-w-2xl mx-auto" data-testid="pacchetto-all-structures-note">
                  L'abbonamento dà accesso a <b className="text-white">tutte le strutture</b> inserite su Hostland: non è un pagamento una tantum per singola casa, ma sblocca le funzioni in <b className="text-white">tutte le tue strutture</b>.
                </p>
              </div>
            </div>
          </motion.div>
        }
        liveDemoLink="/live/welcome-book"
        liveDemoLabel="Guarda il Welcome Book in live"
      />

      {/* ═══ SECTION 3 — PARKHOST ═══ */}
      <section id="parkhost" className="px-4 sm:px-6 py-20 md:py-28 relative overflow-hidden" style={{ background: "linear-gradient(160deg, #EFF6FF 0%, #DBEAFE 55%, #BFDBFE 100%)" }} data-testid="parkhost-section">
        <motion.div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle, #60A5FA88, transparent 70%)" }}
          animate={heavyFx ? { x: [0, 30, 0], y: [0, 20, 0] } : undefined} transition={heavyFx ? { duration: 15, repeat: Infinity, ease: "easeInOut" } : undefined} />

        <motion.div variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-6xl mx-auto relative">
          <motion.div variants={fadeUp} className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/80 border border-blue-200 backdrop-blur-sm">
              <span className="w-5 h-5 rounded-full bg-blue-700 text-white text-xs font-black flex items-center justify-center">P</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-800">{t("lp.pk.badge")}</span>
            </span>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-zinc-900 leading-[1.02] mb-3">
              {t("lp.pk.title_1")} <span style={{ backgroundImage: "linear-gradient(105deg, #1E40AF, #0EA5E9)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("lp.pk.title_grad")}</span> {t("lp.pk.title_2")}
            </h2>
            <p className="text-zinc-600 font-medium text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              {t("lp.pk.sub")}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div variants={fadeUp}>
              <ul className="space-y-3 text-sm text-zinc-800">
                {[
                  [getT({ it: "1 mese gratis, poi €4,99/anno", en: "1 month free, then €4.99/year", fr: "1 mois gratuit, puis 4,99 €/an", es: "1 mes gratis, luego 4,99 €/año", de: "1 Monat gratis, dann 4,99 €/Jahr" }, lang), getT({ it: "Nessuna carta richiesta per il trial", en: "No card required for the trial", fr: "Aucune carte requise pour l'essai", es: "Sin tarjeta para la prueba", de: "Keine Karte für die Testphase nötig" }, lang)],
                  [getT({ it: "Landing SEO-ready", en: "SEO-ready landing", fr: "Landing prête pour le SEO", es: "Landing lista para SEO", de: "SEO-fertige Landingpage" }, lang), getT({ it: "Il tuo parcheggio per iniziare a disintermediare", en: "Your parking spot to start cutting out middlemen", fr: "Votre parking pour commencer à désintermédier", es: "Tu aparcamiento para empezar a desintermediar", de: "Dein Parkplatz, um Vermittler auszuschalten" }, lang)],
                  [getT({ it: "Foto, altezza, ricarica EV, sorveglianza", en: "Photos, height, EV charging, surveillance", fr: "Photos, hauteur, recharge VE, surveillance", es: "Fotos, altura, carga EV, vigilancia", de: "Fotos, Höhe, E-Laden, Überwachung" }, lang), getT({ it: "Tutte le caratteristiche tecniche", en: "All technical features", fr: "Toutes les caractéristiques techniques", es: "Todas las características técnicas", de: "Alle technischen Merkmale" }, lang)],
                  [getT({ it: "Contatti diretti via email/WhatsApp", en: "Direct contacts via email/WhatsApp", fr: "Contacts directs par e-mail/WhatsApp", es: "Contactos directos por email/WhatsApp", de: "Direkte Kontakte per E-Mail/WhatsApp" }, lang), getT({ it: "Zero commissioni, decidi tu il prezzo", en: "Zero commissions, you set the price", fr: "Zéro commission, vous fixez le prix", es: "Cero comisiones, tú pones el precio", de: "Keine Provisionen, du bestimmst den Preis" }, lang)],
                  [getT({ it: "Pin blu sulla mappa dei viaggiatori", en: "Blue pin on the travelers' map", fr: "Épingle bleue sur la carte des voyageurs", es: "Pin azul en el mapa de viajeros", de: "Blauer Pin auf der Reisenden-Karte" }, lang), getT({ it: "Slider dei più cliccati Hostland", en: "Hostland most-clicked slider", fr: "Carrousel des plus cliqués Hostland", es: "Slider de los más clicados de Hostland", de: "Slider der meistgeklickten Hostland" }, lang)],
                ].map(([main, sub], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-blue-700 text-white text-xs flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-3 h-3" />
                    </span>
                    <span>
                      <span className="font-bold text-zinc-900">{main}</span>
                      <span className="block text-[11px] text-zinc-500 mt-0.5">{sub}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <motion.button
                onClick={() => navigate(user ? "/parkhost/enroll" : "/login?mode=signup&as=parkhost")}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.97 }}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-white font-black text-sm md:text-base shadow-[0_12px_28px_-10px_rgba(29,78,216,0.5)]"
                style={{ background: "linear-gradient(135deg, #1E40AF, #0EA5E9)" }}
                data-testid="parkhost-cta"
              >
                <Car className="w-4 h-4" />
                {t("lp.pk.cta")}
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-blue-100 shadow-xl">
              <h3 className="text-lg md:text-xl font-black text-zinc-900 mb-4">{getT({ it: "Perché scegliere Hostland per i tuoi affitti brevi", en: "Why choose Hostland for your short-term rentals", fr: "Pourquoi choisir Hostland pour vos locations courtes", es: "Por qué elegir Hostland para tus alquileres vacacionales", de: "Warum Hostland für deine Kurzzeitvermietung" }, lang)}</h3>
              <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                <p><strong className="text-blue-800">{getT({ it: "Fai soldi con il tuo spazio auto", en: "Make money with your parking space", fr: "Gagnez de l'argent avec votre place de parking", es: "Gana dinero con tu plaza de aparcamiento", de: "Verdiene Geld mit deinem Parkplatz" }, lang)}</strong> {getT({ it: "anche mentre lavori o sei in vacanza. Vicino a stadi, fiere, aeroporti e stazioni la domanda è altissima.", en: "even while you work or are on holiday. Near stadiums, fairs, airports and stations demand is sky-high.", fr: "même pendant que vous travaillez ou êtes en vacances. Près des stades, foires, aéroports et gares, la demande est très forte.", es: "incluso mientras trabajas o estás de vacaciones. Cerca de estadios, ferias, aeropuertos y estaciones la demanda es altísima.", de: "sogar während du arbeitest oder im Urlaub bist. In der Nähe von Stadien, Messen, Flughäfen und Bahnhöfen ist die Nachfrage sehr hoch." }, lang)}</p>
                <p><strong className="text-blue-800">{getT({ it: "Zero commissioni.", en: "Zero commissions.", fr: "Zéro commission.", es: "Cero comisiones.", de: "Keine Provisionen." }, lang)}</strong> {getT({ it: "L'automobilista ti contatta direttamente e concordate voi prezzo e modalità.", en: "The driver contacts you directly and you agree on price and terms.", fr: "L'automobiliste vous contacte directement et vous convenez du prix et des modalités.", es: "El conductor te contacta directamente y acordáis precio y condiciones.", de: "Der Fahrer kontaktiert dich direkt und ihr vereinbart Preis und Bedingungen." }, lang)}</p>
                <p><strong className="text-blue-800">{getT({ it: "SEO integrata.", en: "Built-in SEO.", fr: "SEO intégré.", es: "SEO integrado.", de: "Integriertes SEO." }, lang)}</strong> {getT({ it: "Ogni annuncio ha la sua landing con Schema.org, sitemap, immagine OG.", en: "Each listing has its own landing with Schema.org, sitemap, OG image.", fr: "Chaque annonce a sa landing avec Schema.org, sitemap, image OG.", es: "Cada anuncio tiene su landing con Schema.org, sitemap, imagen OG.", de: "Jede Anzeige hat ihre Landingpage mit Schema.org, Sitemap, OG-Bild." }, lang)}</p>
              </div>
              <div className="mt-5 pt-5 border-t border-blue-100 space-y-2.5 text-xs">
                {[
                  [getT({ it: "Posso affittare se sono in affitto?", en: "Can I rent it out if I'm a tenant?", fr: "Puis-je louer si je suis locataire ?", es: "¿Puedo alquilar si soy inquilino?", de: "Darf ich vermieten, wenn ich Mieter bin?" }, lang), getT({ it: "Sì, ma serve autorizzazione scritta del proprietario o clausola di subaffitto.", en: "Yes, but you need written authorization from the owner or a sublet clause.", fr: "Oui, mais il faut une autorisation écrite du propriétaire ou une clause de sous-location.", es: "Sí, pero necesitas autorización escrita del propietario o cláusula de subarriendo.", de: "Ja, aber du brauchst eine schriftliche Erlaubnis des Eigentümers oder eine Untermietklausel." }, lang)],
                  [getT({ it: "Come vengo pagato?", en: "How do I get paid?", fr: "Comment suis-je payé ?", es: "¿Cómo me pagan?", de: "Wie werde ich bezahlt?" }, lang), getT({ it: "Direttamente dall'automobilista. Hostland non fa da intermediario.", en: "Directly by the driver. Hostland is not an intermediary.", fr: "Directement par l'automobiliste. Hostland n'est pas intermédiaire.", es: "Directamente por el conductor. Hostland no actúa de intermediario.", de: "Direkt vom Fahrer. Hostland ist kein Vermittler." }, lang)],
                  [getT({ it: "Dopo il mese gratuito?", en: "After the free month?", fr: "Après le mois gratuit ?", es: "¿Después del mes gratis?", de: "Nach dem Gratismonat?" }, lang), getT({ it: "€4,99/anno via Stripe. Se non attivi, l'annuncio si disattiva.", en: "€4.99/year via Stripe. If you don't activate, the listing is disabled.", fr: "4,99 €/an via Stripe. Sans activation, l'annonce est désactivée.", es: "4,99 €/año vía Stripe. Si no activas, el anuncio se desactiva.", de: "4,99 €/Jahr über Stripe. Ohne Aktivierung wird die Anzeige deaktiviert." }, lang)],
                ].map(([q, a], i) => (
                  <details key={i} className="cursor-pointer">
                    <summary className="font-bold text-zinc-900">{q}</summary>
                    <p className="mt-1.5 text-zinc-600">{a}</p>
                  </details>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Keyword cloud */}
          <div className="mt-12 text-center">
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-800 mb-3">{getT({ it: "Cerca parcheggi in:", en: "Search parking in:", fr: "Chercher un parking à :", es: "Buscar aparcamiento en:", de: "Parkplatz suchen in:" }, lang)}</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto text-[11px]">
              {["Roma", "Milano", "Firenze", "Napoli", "Venezia", "Torino", "Bologna", "Genova", "Palermo", "Bari", "Verona", "Rimini"].map((c) => (
                <span key={c} className="px-2.5 py-1 rounded-full bg-white/70 border border-blue-100 text-blue-800 font-semibold">
                  {getT({ it: "parcheggio", en: "parking", fr: "parking", es: "aparcamiento", de: "Parkplatz" }, lang)} {c.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ═══ PROPERTY SLIDER — Unified (Case vacanze e B&B su Hostland, max 12, deduped) ═══ */}
      {(mostClicked.length > 0 || latestProps.length > 0) && (() => {
        const _seen = new Set();
        const mergedProps = [...mostClicked, ...latestProps].filter((p) => {
          if (_seen.has(p.property_id)) return false;
          _seen.add(p.property_id);
          return true;
        }).slice(0, 12);
        return (
          <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="py-10" data-testid="home-properties-slider">
            <div className="max-w-6xl mx-auto px-6 mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{t("lp.slider.trending_eye")}</p>
                <h2 className="text-2xl md:text-3xl font-black text-zinc-900 tracking-tight mt-0.5">{t("lp.slider.trending_title")}</h2>
                <p className="text-[11px] text-zinc-400 font-medium mt-1">{t("lp.slider.trending_sub")}</p>
              </div>
              <button onClick={() => navigate("/map")} className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
                {t("lp.slider.see_all")} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <InfiniteSlider items={mergedProps} speed={0.4} trackClick />
          </motion.section>
        );
      })()}

      {/* ═══ EVENTS TICKER removed per user request ═══ */}

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="come-funziona" className="px-6 py-16 bg-white/60">
        <motion.div variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-5xl mx-auto">
          <motion.p variants={fadeUp} className="text-xs font-black uppercase tracking-widest text-center mb-2" style={{ color: ACCENT }}>{t("lp.how.eye")}</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-zinc-900 text-center mb-2 tracking-tighter"><Typewriter text={t("lp.how.title")} /></motion.h2>
          <motion.p variants={fadeUp} className="text-zinc-500 text-center mb-10 font-medium text-sm max-w-lg mx-auto">{t("lp.how.sub")}</motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Home className="w-5 h-5" />, title: "Crea il tuo Sito", desc: "In 2 minuti crei un sito stile Airbnb: foto, mappa, contatti, booking engine, recensioni Google e blog.", color: ACCENT },
              { icon: <QrCode className="w-5 h-5" />, title: "QR Code e Short Link", desc: "QR premium PDF + link personalizzato (/s/villa-marco). Stampi e appendi in casa.", color: "#8B5CF6" },
              { icon: <BookOpen className="w-5 h-5" />, title: "Attiva il Welcome Book Digitale", desc: "Wi-Fi card, manuali casa, concierge, servizi extra, self check-in e SOS emergenze.", color: BLUE },
              { icon: <Star className="w-5 h-5" />, title: "Fidelizza gli Ospiti", desc: "Carta fedeltà a punti, recensioni Google automatiche, contatti diretti host via email o WhatsApp.", color: AMBER },
              { icon: <Gift className="w-5 h-5" />, title: "Carta Fedeltà", desc: "Punti per soggiorno, premi personalizzabili. L'ospite torna direttamente da te.", color: "#10B981" },
              { icon: <Globe className="w-5 h-5" />, title: "5 Lingue Automatiche", desc: "Sito e Welcome Book Digitale tradotti in IT/EN/FR/ES/DE. Rilevamento browser.", color: "#EC4899" },
            ].map((f, i) => (
              <motion.div key={i} variants={fadeUp} custom={i}
                whileHover={{ y: -6, boxShadow: `0 20px 40px ${f.color}15` }}
                className="relative p-5 rounded-2xl border border-zinc-100 bg-white/80 backdrop-blur-sm transition-all group cursor-default"
                data-testid={`how-card-${i}`}>
                <motion.div className="absolute top-4 right-4 text-[44px] font-black leading-none select-none" style={{ color: `${f.color}08` }}>
                  {i + 1}
                </motion.div>
                <motion.div whileHover={{ rotate: [0, -10, 10, 0], scale: 1.15 }} transition={{ duration: 0.5 }} className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3 shadow-sm" style={{ background: `${f.color}12`, color: f.color }}>
                  {f.icon}
                </motion.div>
                <div className="text-sm font-black text-zinc-900 mb-1.5">{f.title}</div>
                <p className="text-xs text-zinc-500 font-medium leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ═══ PRICING — clean & easy ═══ */}
      <section id="prezzi" className="px-6 py-20 md:py-28 relative overflow-hidden">
        <motion.div aria-hidden className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${ACCENT}30, transparent 70%)` }} />
        <motion.div variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-5xl mx-auto relative">
          <motion.p variants={fadeUp} className="text-xs font-black uppercase tracking-widest text-center mb-3" style={{ color: ACCENT }}>{getT({ it: "Prezzi", en: "Pricing", fr: "Tarifs", es: "Precios", de: "Preise" }, lang)}</motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-black text-zinc-900 text-center mb-3 tracking-tighter">{getT({ it: "Prezzi Hostland: prova gratis 30 giorni, poi da 4,99 € al mese", en: "Hostland pricing: 30-day free trial, then from €4.99/month", fr: "Tarifs Hostland : 30 jours gratuits, puis dès 4,99 €/mois", es: "Precios Hostland: 30 días gratis, luego desde 4,99 €/mes", de: "Hostland-Preise: 30 Tage gratis, dann ab 4,99 €/Monat" }, lang)}</motion.h2>
          <motion.p variants={fadeUp} className="text-zinc-500 text-center mb-10 font-medium text-sm max-w-lg mx-auto">
            {getT({ it: "30 giorni gratuiti, pagamenti gestiti da", en: "30 days free, payments handled by", fr: "30 jours gratuits, paiements gérés par", es: "30 días gratis, pagos gestionados por", de: "30 Tage gratis, Zahlungen über" }, lang)} <span className="font-semibold text-zinc-700">Stripe<sup className="text-[8px]">®</sup></span>. {getT({ it: "Cancella quando vuoi.", en: "Cancel anytime.", fr: "Annulez quand vous voulez.", es: "Cancela cuando quieras.", de: "Jederzeit kündbar." }, lang)}
          </motion.p>

          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {/* Monthly — Hostland Completo (Sito + Welcome) */}
            <motion.div variants={scaleIn} custom={0} className="rounded-3xl border border-zinc-200 bg-white p-6 flex flex-col" data-testid="pricing-monthly">
              <p className="text-[9px] font-black uppercase tracking-[0.15em] mb-1" style={{ color: ACCENT }}>{getT({ it: "Hostland Completo", en: "Hostland Complete", fr: "Hostland Complet", es: "Hostland Completo", de: "Hostland Komplett" }, lang)}</p>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 mb-2">{getT({ it: "Mensile", en: "Monthly", fr: "Mensuel", es: "Mensual", de: "Monatlich" }, lang)}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-zinc-900">€9,90</span>
                <span className="text-zinc-400 text-sm font-medium">{getT({ it: "/mese", en: "/month", fr: "/mois", es: "/mes", de: "/Monat" }, lang)}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mb-5">{getT({ it: "Sito web + Welcome Book completo", en: "Website + full Welcome Book", fr: "Site web + Welcome Book complet", es: "Sitio web + Welcome Book completo", de: "Website + komplettes Welcome Book" }, lang)}</p>
              <ul className="space-y-2 mb-6 text-sm flex-1">
                {[
                  { it: "2 strutture incluse", en: "2 properties included", fr: "2 logements inclus", es: "2 alojamientos incluidos", de: "2 Unterkünfte inklusive" },
                  { it: "Sito + Welcome Book Digitale completo", en: "Website + full Digital Welcome Book", fr: "Site + Welcome Book numérique complet", es: "Sitio + Welcome Book Digital completo", de: "Website + komplettes digitales Welcome Book" },
                  { it: "Recensioni Google", en: "Google reviews", fr: "Avis Google", es: "Reseñas de Google", de: "Google-Bewertungen" },
                  { it: "Blog + Analytics", en: "Blog + Analytics", fr: "Blog + Analytics", es: "Blog + Analytics", de: "Blog + Analytics" },
                  { it: "Traduzioni 5 lingue", en: "5-language translations", fr: "Traductions en 5 langues", es: "Traducciones en 5 idiomas", de: "Übersetzungen in 5 Sprachen" },
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-zinc-600 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: ACCENT }} />
                    <span>{getT(item, lang)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-zinc-500 mb-4">{t("lp.price.per_extra")}</p>
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => navigate("/login?as=host&plan=monthly")}
                className="w-full rounded-full py-3 font-black text-sm border-2 transition-all" style={{ borderColor: ACCENT, color: ACCENT }} data-testid="pricing-monthly-btn">
                {t("lp.price.activate_m")}
              </motion.button>
            </motion.div>

            {/* Yearly — highlighted CONSIGLIATO (center) */}
            <motion.div variants={scaleIn} custom={1} className="rounded-3xl bg-white p-6 relative flex flex-col" style={{ border: `2px solid ${ACCENT}`, boxShadow: `0 20px 60px -20px ${ACCENT}55` }} data-testid="pricing-yearly">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-white text-[10px] font-black px-3 py-1 rounded-full shadow" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})` }}>{getT({ it: "RISPARMIA 50%", en: "SAVE 50%", fr: "ÉCONOMISEZ 50%", es: "AHORRA 50%", de: "SPARE 50%" }, lang)}</span>
              </div>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] mb-1" style={{ color: ACCENT }}>{getT({ it: "Hostland Completo", en: "Hostland Complete", fr: "Hostland Complet", es: "Hostland Completo", de: "Hostland Komplett" }, lang)}</p>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: ACCENT }}>{getT({ it: "Annuale · Consigliato", en: "Yearly · Recommended", fr: "Annuel · Recommandé", es: "Anual · Recomendado", de: "Jährlich · Empfohlen" }, lang)}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-zinc-900">€59,90</span>
                <span className="text-zinc-400 text-sm font-medium">{getT({ it: "/anno", en: "/year", fr: "/an", es: "/año", de: "/Jahr" }, lang)}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mb-5">{getT({ it: "Solo €4,99/mese", en: "Just €4.99/month", fr: "Seulement 4,99 €/mois", es: "Solo 4,99 €/mes", de: "Nur 4,99 €/Monat" }, lang)}</p>
              <ul className="space-y-2 mb-6 text-sm flex-1">
                {[
                  { it: "Strutture illimitate", en: "Unlimited properties", fr: "Logements illimités", es: "Alojamientos ilimitados", de: "Unbegrenzte Unterkünfte" },
                  { it: "Tutto del piano Mensile", en: "Everything in Monthly", fr: "Tout du plan Mensuel", es: "Todo del plan Mensual", de: "Alles aus dem Monatsplan" },
                  { it: "Priorità supporto", en: "Priority support", fr: "Support prioritaire", es: "Soporte prioritario", de: "Priorisierter Support" },
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-zinc-600 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: ACCENT }} />
                    <span>{getT(item, lang)}</span>
                  </li>
                ))}
                <li className="flex items-start gap-2 text-zinc-900 font-black pt-2 border-t border-zinc-100 mt-2">
                  <Gift className="w-4 h-4 shrink-0 mt-0.5" style={{ color: ACCENT }} />
                  <span>{getT({ it: "1h con", en: "1h with", fr: "1h avec", es: "1h con", de: "1 Std. mit" }, lang)} <span style={{ color: ACCENT }}>Sergio</span> {getT({ it: "su Google Meet", en: "on Google Meet", fr: "sur Google Meet", es: "en Google Meet", de: "auf Google Meet" }, lang)} <span className="text-[10px] font-normal text-zinc-400 block">{getT({ it: "solo nell'annuale", en: "yearly plan only", fr: "plan annuel uniquement", es: "solo en el plan anual", de: "nur im Jahresplan" }, lang)}</span></span>
                </li>
              </ul>
              <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }} onClick={() => navigate("/login?as=host&plan=yearly")}
                className="w-full text-white rounded-full py-3 font-black text-sm transition-all hover:opacity-90 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})` }} data-testid="pricing-yearly-btn">
                {t("lp.price.activate_y")}
              </motion.button>
            </motion.div>

            {/* Solo Welcome Book — NUOVO prodotto €35,90/anno (destra) */}
            <motion.div variants={scaleIn} custom={2} className="rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50/80 to-orange-50/60 p-6 flex flex-col relative" data-testid="pricing-guestbook-only">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-white text-[10px] font-black px-3 py-1 rounded-full shadow bg-gradient-to-r from-amber-500 to-orange-500 whitespace-nowrap">✨ {getT({ it: "NOVITÀ", en: "NEW", fr: "NOUVEAU", es: "NOVEDAD", de: "NEU" }, lang)}</span>
              </div>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-amber-700 mb-1">{getT({ it: "Nuovo prodotto", en: "New product", fr: "Nouveau produit", es: "Nuevo producto", de: "Neues Produkt" }, lang)}</p>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-amber-600 mb-2">{getT({ it: "Solo Welcome Book · Annuale", en: "Welcome Book only · Yearly", fr: "Welcome Book seul · Annuel", es: "Solo Welcome Book · Anual", de: "Nur Welcome Book · Jährlich" }, lang)}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-zinc-900">€35,90</span>
                <span className="text-zinc-400 text-sm font-medium">{getT({ it: "/anno", en: "/year", fr: "/an", es: "/año", de: "/Jahr" }, lang)}</span>
              </div>
              <p className="text-[11px] text-amber-800 mb-5 font-semibold">{getT({ it: "Solo €2,99/mese · 30 giorni prova gratis", en: "Just €2.99/month · 30-day free trial", fr: "Seulement 2,99 €/mois · 30 jours d'essai gratuit", es: "Solo 2,99 €/mes · 30 días de prueba gratis", de: "Nur 2,99 €/Monat · 30 Tage gratis testen" }, lang)}</p>
              <ul className="space-y-2 mb-6 text-sm flex-1">
                {[
                  { it: "Fino a 3 Welcome Book inclusi · poi €9,90 l'uno una tantum", en: "Up to 3 Welcome Books included · then €9.90 each one-off", fr: "Jusqu'à 3 Welcome Books inclus · puis 9,90 € chacun", es: "Hasta 3 Welcome Books incluidos · luego 9,90 € cada uno", de: "Bis zu 3 Welcome Books inklusive · dann je 9,90 € einmalig" },
                  { it: "Welcome Book digitale multilingua", en: "Multilingual digital Welcome Book", fr: "Welcome Book numérique multilingue", es: "Welcome Book digital multilingüe", de: "Mehrsprachiges digitales Welcome Book" },
                  { it: "Wi-Fi card + manuali casa + video", en: "Wi-Fi card + house manuals + video", fr: "Carte Wi-Fi + manuels maison + vidéo", es: "Tarjeta Wi-Fi + manuales de la casa + vídeo", de: "WLAN-Karte + Hausanleitungen + Video" },
                  { it: "AI Tour turistico personalizzato", en: "Personalized AI tourist tour", fr: "Visite touristique IA personnalisée", es: "Tour turístico con IA personalizado", de: "Personalisierte KI-Tour" },
                  { it: "Contatti host diretti (email, WhatsApp)", en: "Direct host contacts (email, WhatsApp)", fr: "Contacts hôte directs (e-mail, WhatsApp)", es: "Contactos directos del anfitrión (email, WhatsApp)", de: "Direkte Gastgeber-Kontakte (E-Mail, WhatsApp)" },
                  { it: "Zero PDF · vera WebApp moderna", en: "Zero PDFs · a real modern WebApp", fr: "Zéro PDF · une vraie WebApp moderne", es: "Cero PDF · una WebApp moderna de verdad", de: "Keine PDFs · eine echte moderne WebApp" },
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-zinc-700 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                    <span>{getT(item, lang)}</span>
                  </li>
                ))}
              </ul>
              <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }} onClick={() => navigate("/login?as=host&plan=guestbook_only")}
                className="w-full rounded-full py-3 font-black text-sm text-white shadow-lg transition-all hover:opacity-95"
                style={{ background: "linear-gradient(135deg, #F59E0B, #EA580C)" }} data-testid="pricing-guestbook-only-btn">
                {getT({ it: "Attiva Welcome Book", en: "Activate Welcome Book", fr: "Activer le Welcome Book", es: "Activar Welcome Book", de: "Welcome Book aktivieren" }, lang)}
              </motion.button>
            </motion.div>
          </div>

          {/* ParkHost mini-plan */}
          <motion.div variants={fadeUp} className="mt-6 max-w-4xl mx-auto rounded-2xl bg-blue-50 border border-blue-200 p-5 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center font-black text-lg shrink-0">P</div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm font-black text-blue-900">{t("lp.price.pk_title")}</p>
              <p className="text-[11px] text-blue-700 font-medium">{t("lp.price.pk_sub")}</p>
            </div>
            <button onClick={() => navigate(user ? "/parkhost/enroll" : "/login?mode=signup&as=parkhost")} className="rounded-full px-5 py-2 text-white font-black text-xs shadow" style={{ background: "linear-gradient(135deg, #1E40AF, #0EA5E9)" }}>
              {t("lp.price.pk_join")}
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ NOVITÀ · Pre check-in Hostland CONTROL (documenti → Alloggiati Web + ROSS 1000) ═══ */}
      <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="px-6 pt-0 pb-12" data-testid="precheckin-banner-section">
        <div className="max-w-5xl mx-auto rounded-3xl relative overflow-hidden" style={{ background: "#17120f" }}>
          <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 25%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
          <div aria-hidden className="absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #D7362B, transparent 70%)" }} />
          <div className="relative z-10 grid md:grid-cols-[1.4fr_1fr] gap-8 p-8 md:p-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-red-600/15 border border-red-500/30 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-red-300" data-testid="precheckin-badge">
                <Sparkles className="w-3.5 h-3.5" /> Novità · Hostland CONTROL
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter mt-4 leading-tight">
                Pre check-in e invio documenti, in automatico
              </h2>
              <p className="text-zinc-300 text-sm md:text-base font-medium mt-3 max-w-xl">
                Chiedi i documenti ai tuoi ospiti prima dell'arrivo con un semplice link e invia tutto ad <b className="text-white">Alloggiati Web</b> e <b className="text-white">ROSS 1000</b> senza stress. Dati cifrati e cancellati subito dopo l'invio.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {[
                  "Richiesta documenti agli ospiti via link",
                  "Invio automatico ad Alloggiati Web",
                  "Invio automatico a ROSS 1000",
                  "Notifiche Telegram a ogni passaggio",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-zinc-200 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <div className="flex items-baseline gap-1.5 rounded-2xl bg-white/5 border border-white/10 px-4 py-2.5" data-testid="precheckin-price">
                  <span className="text-2xl font-black text-white">19,90&nbsp;€</span>
                  <span className="text-xs font-bold text-zinc-400">/ anno</span>
                </div>
                <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.03 }} onClick={() => navigate(user ? "/dashboard" : "/login?as=host")}
                  className="rounded-full px-6 py-3 font-black text-sm text-white shadow-lg inline-flex items-center gap-2" style={{ background: "linear-gradient(135deg, #D7362B, #991b1b)" }} data-testid="precheckin-cta-btn">
                  <ShieldCheck className="w-4 h-4" /> Scopri il Pre check-in <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-center">
              <div className="relative w-full max-w-[240px] aspect-[3/4] rounded-3xl bg-gradient-to-br from-zinc-800 to-zinc-900 border border-white/10 shadow-2xl p-6 flex flex-col items-center justify-center gap-4">
                <span className="w-20 h-20 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg"><ShieldCheck className="w-10 h-10" /></span>
                <p className="text-white font-black text-center text-lg leading-tight">Registra i documenti</p>
                <div className="w-full space-y-2">
                  <div className="h-2.5 rounded-full bg-white/10" />
                  <div className="h-2.5 rounded-full bg-white/10 w-4/5" />
                  <div className="h-2.5 rounded-full bg-white/10 w-3/5" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-300"><Send className="w-3.5 h-3.5" /> Inviato alla Questura</span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ═══ NOVITÀ · DeVisu — identificazione in videochiamata + QR code (plugin Welcome Book) ═══ */}
      <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="px-6 pt-0 pb-14" data-testid="devisu-banner-section">
        <div className="max-w-5xl mx-auto rounded-3xl relative overflow-hidden" style={{ background: "#0A1222" }}>
          <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 25%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
          <div aria-hidden className="absolute -top-24 -left-16 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #2F6FE0, transparent 70%)" }} />
          <div className="relative z-10 grid md:grid-cols-[1fr_1.4fr] gap-8 p-8 md:p-12 items-center">
            {/* Mockup QR + video */}
            <div className="hidden md:flex items-center justify-center order-2 md:order-1">
              <div className="relative w-full max-w-[240px] aspect-[3/4] rounded-3xl bg-gradient-to-br from-blue-950 to-zinc-900 border border-white/10 shadow-2xl p-6 flex flex-col items-center justify-center gap-4">
                <span className="w-20 h-20 rounded-full text-white flex items-center justify-center shadow-lg" style={{ background: "#2F6FE0" }}><Video className="w-10 h-10" /></span>
                <p className="text-white font-black text-center text-lg leading-tight">Identifica in 30 secondi</p>
                <span className="w-24 h-24 rounded-2xl bg-white flex items-center justify-center shadow-lg"><QrCode className="w-16 h-16 text-zinc-900" /></span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-300"><ShieldCheck className="w-3.5 h-3.5" /> Nessuna foto salvata</span>
              </div>
            </div>
            {/* Testo */}
            <div className="order-1 md:order-2">
              <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ background: "rgba(47,111,224,0.15)", borderColor: "rgba(47,111,224,0.35)", color: "#93B4F5" }} data-testid="devisu-badge">
                <Sparkles className="w-3.5 h-3.5" /> Plugin Welcome Book · DeVisu
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter mt-4 leading-tight">
                Identificazione in videochiamata con QR code
              </h2>
              <p className="text-zinc-300 text-sm md:text-base font-medium mt-3 max-w-xl">
                L'ospite inquadra il <b className="text-white">QR code</b> alla porta e ti chiama: lo riconosci in diretta e sblocchi l'accesso. La tua camera resta spenta, la chiamata <b className="text-white">non viene registrata</b> e nessuna foto viene salvata.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {[
                  "Videochiamata di identificazione all'arrivo",
                  "QR code alla porta, pronto da stampare",
                  "La tua camera è sempre spenta",
                  "Notifiche Telegram e push in tempo reale",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-zinc-200 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#60A5FA" }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <div className="flex items-baseline gap-1.5 rounded-2xl bg-white/5 border border-white/10 px-4 py-2.5" data-testid="devisu-price">
                  <span className="text-2xl font-black text-white">29,90&nbsp;€</span>
                  <span className="text-xs font-bold text-zinc-400">/ anno</span>
                </div>
                <motion.button whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.03 }} onClick={() => navigate(user ? "/dashboard/devisu" : "/login?as=host")}
                  className="rounded-full px-6 py-3 font-black text-sm text-white shadow-lg inline-flex items-center gap-2" style={{ background: "linear-gradient(135deg, #2F6FE0, #1E40AF)" }} data-testid="devisu-cta-btn">
                  <Video className="w-4 h-4" /> Scopri DeVisu <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      <section className="px-6 py-10" data-testid="about-hostland">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-zinc-900 tracking-tight mb-3">Chi c'è dietro Hostland</h2>
          <p className="text-zinc-600 font-medium text-sm md:text-base leading-relaxed mb-4">
            Hostland è creato da <strong>Vita da Host</strong>, la community italiana di host e property manager. Strumenti pensati da chi ospita ogni giorno, per far crescere le prenotazioni dirette senza commissioni.{" "}
            <a href="https://www.vitadahost.it/chi-siamo" target="_blank" rel="noopener noreferrer" className="font-bold underline decoration-2 underline-offset-2" style={{ color: ACCENT }}>Scopri chi siamo</a>.
          </p>
          <p className="text-[11px] font-black uppercase tracking-widest text-zinc-400 mb-3">Ne hanno parlato</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm font-bold text-zinc-700">
            <a href="https://www.repubblica.it/economia/2026/01/17/news/affitti_brevi_nasce_la_risposta_italiana_ad_airbnb_zero_commissioni_per_i_proprietari-425100665/" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-900 transition-colors">la Repubblica</a>
            <span className="text-zinc-300" aria-hidden="true">·</span>
            <a href="https://www.ilsecoloxix.it/liguria/news/come-funziona-vieni-da-noi-la-risposta-ligure-ad-airbnb-e-booking-fhtj5xd0/" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-900 transition-colors">Il Secolo XIX</a>
            <span className="text-zinc-300" aria-hidden="true">·</span>
            <a href="https://www.lanazione.it/firenze/cronaca/la-rivolta-degli-host-contro-f517ad4b" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-900 transition-colors">La Nazione</a>
            <span className="text-zinc-300" aria-hidden="true">·</span>
            <a href="https://travelnostop.com/news/affitti-brevi/affitti-brevi-vita-da-host-lancia-la-sfida-alle-grandi-ota_681613" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-900 transition-colors">Travelnostop</a>
            <span className="text-zinc-300" aria-hidden="true">·</span>
            <a href="https://www.condominionotizie.it/affitti-bnb/affitti-brevi-vienidanoi/" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-900 transition-colors">Condominio Notizie</a>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <motion.section variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="px-6 pt-14 pb-4" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.p variants={fadeUp} className="text-xs font-black uppercase tracking-widest text-center mb-2" style={{ color: ACCENT }}>{t("lp.faq.eye")}</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-zinc-900 text-center mb-2 tracking-tighter"><Typewriter text={t("lp.faq.title")} /></motion.h2>
          <motion.p variants={fadeUp} className="text-zinc-500 text-center mb-8 font-medium text-sm">{t("lp.faq.sub")}</motion.p>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <FAQItem key={i} question={getT(f.q, lang)} answer={getT(f.a, lang)} index={i} />
            ))}
          </div>
        </div>
      </motion.section>

      {/* ═══ JSON-LD Structured Data — SEO grade A ═══ */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Organization",
        "name": "Hostland", "alternateName": "Hostland by Vita da Host",
        "url": "https://hostland.vitadahost.it",
        "logo": "https://hostland.vitadahost.it/hostland-logo.png",
        "description": "Piattaforma italiana per host, property manager e viaggiatori: siti web, Welcome Book digitale multilingua, QR code, self check-in e ParkHost. Zero commissioni.",
        "parentOrganization": { "@type": "Organization", "name": "Vita da Host", "url": "https://www.vitadahost.it" },
        "sameAs": ["https://www.vitadahost.it", "https://vitadahost.it/servizi/hostland", "https://www.instagram.com/vita_da_host_/", "https://www.facebook.com/VitadaHostUfficiale/", "https://www.tiktok.com/@vita.da.host", "https://www.youtube.com/@VitadaHostChannel/", "https://www.linkedin.com/in/sergio-battelli-vitadahost/"],
        "address": { "@type": "PostalAddress", "addressCountry": "IT" },
        "contactPoint": { "@type": "ContactPoint", "contactType": "customer service", "email": "vitadahost@gmail.com", "availableLanguage": ["Italian", "English", "French", "German", "Spanish"] }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebSite",
        "@id": "https://hostland.vitadahost.it/#website",
        "name": "Hostland", "alternateName": "Hostland by Vita da Host",
        "url": "https://hostland.vitadahost.it/", "inLanguage": ["it", "en", "fr", "de", "es"],
        "publisher": { "@type": "Organization", "name": "Vita da Host", "url": "https://www.vitadahost.it" },
        "potentialAction": {
          "@type": "SearchAction",
          "target": { "@type": "EntryPoint", "urlTemplate": "https://hostland.vitadahost.it/search?istat={search_term_string}" },
          "query-input": "required name=search_term_string"
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://hostland.vitadahost.it/" }
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "SoftwareApplication",
        "@id": "https://hostland.vitadahost.it/#software",
        "name": "Hostland", "applicationCategory": "BusinessApplication",
        "applicationSubCategory": "Property Management, Hospitality",
        "operatingSystem": "Web",
        "offers": [
          { "@type": "Offer", "name": "Prova gratuita 30 giorni", "price": "0", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host" },
          { "@type": "Offer", "name": "Completo mensile", "price": "9.90", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host" },
          { "@type": "Offer", "name": "Completo annuale", "price": "59.90", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host" },
          { "@type": "Offer", "name": "Solo Welcome Book annuale", "price": "35.90", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host&plan=guestbook_only" },
          { "@type": "Offer", "name": "Add-on Alloggiati Web e ROSS 1000", "price": "19.90", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host" },
          { "@type": "Offer", "name": "ParkHost annuale", "price": "4.99", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/parkhost/enroll" },
          { "@type": "Offer", "name": "Casa aggiuntiva (una tantum)", "price": "1.00", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://hostland.vitadahost.it/login?as=host" }
        ],
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "128", "bestRating": "5", "worstRating": "1" },
        "description": "Hostland: piattaforma italiana per creare il sito della propria struttura con prenotazioni dirette senza commissioni, il Welcome Book la guida digitale per ospiti in 5 lingue, e il check-in online con invio automatico ad Alloggiati Web e ROSS 1000.",
        "url": "https://hostland.vitadahost.it",
        "author": { "@type": "Organization", "name": "Vita da Host", "url": "https://www.vitadahost.it" },
        "featureList": "Sito della struttura con prenotazione diretta, Welcome Book la guida digitale per ospiti in 5 lingue, Check-in online con invio automatico ad Alloggiati Web e ROSS 1000, Calendari iCal sincronizzati con Airbnb e Booking, Codici d'accesso e apertura porta con smart lock, QR code, Tour con guida AI, Allerte meteo, Tassa di soggiorno online, Recensioni Google, ParkHost"
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Posso creare tour su misura per i miei ospiti?", "acceptedAnswer": { "@type": "Answer", "text": "Sì, con i Tour consigliati di Hostland, una funzione mai vista prima. Scegli il mezzo (a piedi, in bici, in moto, in auto o in barca), inserisci partenza e arrivo e Hostland calcola percorso reale, distanza e dislivello su mappa interattiva. L'AI scrive descrizione, tappe, consigli e cosa portare in pochi secondi, e tu modifichi tutto. Ogni tour è tradotto in 5 lingue e appare nel Welcome Book con la mappa del percorso." }},
          { "@type": "Question", "name": "Cos'è Hostland e a chi serve?", "acceptedAnswer": { "@type": "Answer", "text": "Hostland è la piattaforma italiana per host, property manager e viaggiatori. Gli host creano landing page professionali, il Welcome Book Digitale per gli ospiti e QR code personalizzati. I viaggiatori cercano alloggi e contattano direttamente l'host, senza intermediari." }},
          { "@type": "Question", "name": "Cos'è il Welcome Book Digitale?", "acceptedAnswer": { "@type": "Answer", "text": "Una WebApp esclusiva accessibile via QR code: Wi-Fi 1-tap, manuali interattivi, Local Concierge con foto Google e sconti, servizi extra, self check-in digitale e pulsante SOS emergenze. Zero PDF, tutto già tradotto in 5 lingue." }},
          { "@type": "Question", "name": "Quanto costa Hostland?", "acceptedAnswer": { "@type": "Answer", "text": "€9,90/mese (2 strutture) o €59,90/anno con strutture illimitate. 30 giorni di prova gratuita, pagamenti Stripe®. ParkHost €4,99/anno dopo il primo mese gratis." }},
          { "@type": "Question", "name": "Posso trovare alloggi come viaggiatore?", "acceptedAnswer": { "@type": "Answer", "text": "Sì. In homepage attiva il toggle Viaggiatore e cerca per comune, ospiti e date. Contatti direttamente l'host, senza commissioni." }},
          { "@type": "Question", "name": "Come funziona il Booking Engine integrato?", "acceptedAnswer": { "@type": "Answer", "text": "Se hai Lodgify, Smoobu, Octorate, Guesty, Beds24 o Hospitable, incolli il codice HTML e il widget di prenotazione appare nella tua landing. Zero commissioni." }},
          { "@type": "Question", "name": "Le recensioni Google si importano?", "acceptedAnswer": { "@type": "Answer", "text": "Sì, inserisci l'URL del tuo profilo Google Business. Aggiornamento automatico ogni 7 giorni con stelle e data." }},
          { "@type": "Question", "name": "Cos'è ParkHost?", "acceptedAnswer": { "@type": "Answer", "text": "Il modulo Hostland per chi ha un posto auto, box, garage o giardino privato inutilizzato. Crei la landing, ricevi contatti diretti, guadagni senza commissioni. Prima registrazione gratis, poi €4,99/anno." }},
          { "@type": "Question", "name": "Posso inviare i documenti degli ospiti ad Alloggiati Web e ROSS 1000?", "acceptedAnswer": { "@type": "Answer", "text": "Sì, con il Pre check-in di Hostland CONTROL: l'ospite registra i documenti da un link prima dell'arrivo e Hostland li invia automaticamente ad Alloggiati Web (Questura) e a ROSS 1000, con notifiche Telegram a ogni passaggio. Dati cifrati e cancellati dopo l'invio. 19,90 € l'anno." }},
          { "@type": "Question", "name": "Quanto costa il Pre check-in di Hostland e come funziona?", "acceptedAnswer": { "@type": "Answer", "text": "Il Pre check-in costa 19,90 € l'anno per account (tutte le strutture) e si attiva da Hostland CONTROL. Crei la prenotazione, invii il link all'ospite che registra i documenti, e Hostland li trasmette automaticamente ad Alloggiati Web e ROSS 1000. Indirizzo e codici keybox si sbloccano solo dopo la registrazione." }},
          { "@type": "Question", "name": "I dati dei documenti degli ospiti sono al sicuro?", "acceptedAnswer": { "@type": "Answer", "text": "Sì. I dati degli ospiti sono cifrati e cancellati automaticamente subito dopo l'invio ad Alloggiati Web e ROSS 1000. Hostland non conserva documenti né immagini, salva solo l'esito dell'invio, nel rispetto del GDPR." }},
          { "@type": "Question", "name": "Posso provare gratis Hostland?", "acceptedAnswer": { "@type": "Answer", "text": "Sì, 30 giorni di prova gratuita. Nessuna carta richiesta. Pagamenti gestiti da Stripe®." }}
        ]
      }) }} />

      {/* ═══ FINAL CTA STRIP ═══ */}
      <motion.section initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-120px" }} transition={{ duration: 0.6 }} className="px-6 pt-2 pb-14">
        <div className="max-w-4xl mx-auto rounded-3xl p-10 md:p-14 text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)` }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          <div className="relative z-10">
            <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.2em] mb-3">{t("lp.final.eye")}</p>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tighter"><Typewriter text={t("lp.final.title")} /></h2>
            <p className="text-white/80 text-sm md:text-base font-medium max-w-lg mx-auto mb-8">
              {t("lp.final.sub")}
            </p>
            <motion.button whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.03 }} onClick={() => navigate("/login?as=host")}
              className="bg-white font-black px-8 py-4 rounded-full text-sm hover:bg-white/90 transition-colors inline-flex items-center gap-2" style={{ color: ACCENT }} data-testid="trial-cta-btn">
              <Sparkles className="w-4 h-4" />
              {t("lp.final.cta")}
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </motion.section>

      {/* ═══ FOOTER — restyled with newsletter, social badges, graphical columns ═══ */}
      <footer className="relative bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-zinc-300 pt-16 pb-8 overflow-hidden" data-testid="landing-footer">
        {/* Ambient orbs */}
        <div aria-hidden className="absolute -top-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle, ${ACCENT}, transparent 70%)` }} />
        <div aria-hidden className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: `radial-gradient(circle, ${AMBER}, transparent 70%)` }} />
        <div aria-hidden className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 70%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

        <div className="relative max-w-6xl mx-auto px-6">
          {/* ─── Newsletter block ─── */}
          <div className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl p-6 md:p-8 mb-12 grid md:grid-cols-[1.2fr_1fr] gap-6 items-center">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: AMBER }}>Newsletter Vita da Host</p>
              <h3 className="text-2xl md:text-3xl font-black text-white tracking-tighter mb-2">Le novità del mondo host, ogni settimana.</h3>
              <p className="text-sm text-white/60 font-medium leading-relaxed">Guide, aggiornamenti normativi, trucchi per la disintermediazione. Zero spam.</p>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const email = fd.get("nl_email");
              if (!email) return;
              const btn = e.target.querySelector("button[type=submit]");
              const initial = btn.textContent;
              btn.disabled = true;
              btn.textContent = "…";
              try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email, source: "hostland-landing-footer" }),
                });
                const data = await r.json();
                if (data.queued) {
                  btn.textContent = "✓ Iscritto";
                  e.target.reset();
                } else {
                  btn.textContent = "Riprova";
                }
              } catch {
                btn.textContent = "Riprova";
              } finally {
                setTimeout(() => { btn.disabled = false; btn.textContent = initial; }, 2500);
              }
            }} className="flex flex-col sm:flex-row gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                name="nl_email"
                required
                placeholder="La tua email"
                aria-label="Email newsletter"
                className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 font-medium"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                className="rounded-full px-6 py-3 font-black text-sm text-white shadow-lg inline-flex items-center justify-center gap-2 whitespace-nowrap"
                style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)` }}
                data-testid="footer-newsletter-submit"
              >
                <Sparkles className="w-4 h-4" />
                Iscriviti
              </button>
            </form>
          </div>

          {/* ─── Main columns ─── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/hostland-logo.png" alt="Logo Hostland" className="w-9 h-9 rounded-lg object-cover" />
                <span className="font-black text-white text-lg tracking-tight">Hostland</span>
              </div>
              <p className="text-xs text-white/60 font-medium leading-relaxed mb-4">
                La piattaforma italiana per host, property manager e viaggiatori. Landing page, Welcome Book Digitale, QR code, ParkHost — zero commissioni.
              </p>
              {/* Social badges */}
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Instagram", href: "https://www.instagram.com/vita_da_host_/", svg: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
                  { label: "Facebook", href: "https://www.facebook.com/VitadaHostUfficiale/", svg: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
                  { label: "LinkedIn", href: "https://www.linkedin.com/in/sergio-battelli-vitadahost/", svg: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                  { label: "YouTube", href: "https://youtube.com/@vitadahostchannel", svg: "M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" },
                  { label: "TikTok", href: "https://www.tiktok.com/@vita.da.host", svg: "M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z" },
                ].map(s => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 hover:bg-white/15 hover:border-white/30 transition-all group"
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white/70 group-hover:fill-white transition-colors" aria-hidden><path d={s.svg} /></svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Prodotto */}
            <div>
              <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-3">Prodotto</p>
              <div className="flex flex-col gap-2">
                <a href="#sito" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Sito Web</a>
                <a href="/welcome-book-digitale" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Welcome Book</a>
                <a href="#parkhost" className="text-xs text-white/70 hover:text-white transition-colors font-medium">ParkHost</a>
                <a href="#prezzi" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Prezzi</a>
                <a href="/leaderboard" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Classifica Host</a>
                <a href="/login" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Accedi</a>
              </div>
            </div>

            {/* SEO landings */}
            <div>
              <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-3">Welcome Book per</p>
              <div className="flex flex-col gap-2">
                <a href="/guestbook-affitti-brevi" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Affitti Brevi</a>
                <a href="/guestbook-hotel" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Hotel</a>
                <a href="/guestbook-bnb" className="text-xs text-white/70 hover:text-white transition-colors font-medium">B&B</a>
                <a href="/guestbook-casa-vacanza" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Case Vacanza</a>
                <a href="/guestbook-agriturismo" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Agriturismi</a>
                <a href="/guestbook-boutique-hotel" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Boutique Hotel</a>
                <a href="/guestbook-glamping" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Glamping</a>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-3">Sito Web per</p>
              <div className="flex flex-col gap-2">
                <a href="/sito-web-per-host" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Host</a>
                <a href="/sito-web-property-manager" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Property Manager</a>
                <a href="/crea-sito-casa-vacanza" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Casa Vacanza</a>
                <a href="/sito-web-bb" className="text-xs text-white/70 hover:text-white transition-colors font-medium">B&B</a>
                <a href="/sito-web-agriturismo" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Agriturismo</a>
                <a href="/prenotazioni-dirette-senza-commissioni" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Zero commissioni</a>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-3">Risorse</p>
              <div className="flex flex-col gap-2">
                <a href="/guide" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Guide Host</a>
                <a href="/guide/locazioni-turistiche" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Locazioni Turistiche</a>
                <a href="/guide/burocrazia-affitti-brevi" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Burocrazia</a>
                <a href="https://www.vitadahost.it/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Vita da Host</a>
                <a href="/privacy" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Privacy</a>
                <a href="/terms" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Termini</a>
                <a href="/cookie-policy" className="text-xs text-white/70 hover:text-white transition-colors font-medium">Cookie</a>
              </div>
            </div>
          </div>

          {/* SEO long-tail text — hidden but crawlable */}
          <div className="border-t border-white/10 pt-6 mb-4">
            <p className="text-[10px] text-white/40 leading-relaxed font-medium">
              Hostland — Piattaforma italiana per host, property manager e viaggiatori: case vacanza, B&B, affittacamere, agriturismi, residence, boutique hotel, glamping, guesthouse. Welcome Book Digitale con Wi-Fi card, manuali interattivi, Local Concierge con foto Google e sconti, servizi extra, raccolta rifiuti, emergenze SOS. QR code premium, recensioni Google automatiche, carta fedeltà, blog, contatti host diretti (email, WhatsApp), traduzioni automatiche AI in 5 lingue. Modulo ParkHost per l'affitto di posti auto privati. Disponibile in tutta Italia — Liguria, Toscana, Sardegna, Sicilia, Puglia, Costiera Amalfitana, Lago di Garda, Como, Dolomiti, Cinque Terre, Roma, Milano, Firenze, Napoli, Venezia, Bologna, Palermo, Cagliari, Torino, Genova, Bari, Verona, Rimini.
            </p>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t border-white/5">
            <p className="text-white/40 font-medium text-[11px]">© 2026 Hostland by <a href="https://www.vitadahost.it/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors underline">Vita da Host</a>. Tutti i diritti riservati.</p>
            <p className="text-white/40 font-medium text-[11px] inline-flex items-center gap-2">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> P.IVA 02903680995</span>
              <span className="text-white/20">·</span>
              <span>hostland.vitadahost.it</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
